import { Injectable, OnDestroy, inject } from '@angular/core';
import { BCAReports, BPTable, CCEDewPointReports, CCEReports, CVDReportData, DLEReportData, DPTable, FractionUpdate, LabTestReportData, MSSTReportData, ParaffinReports, SummaryOfCCEReports, viscosityReports } from '../../models/lab-test';
import { Borders, Workbook, Worksheet } from 'exceljs';
import * as fs from 'file-saver';
import { Subject, takeUntil } from 'rxjs';
import { ReportContentTable, ReservoirProperties, ReservoirPropertiesDST, ReservoirPropertiesSeparator, SampleAnalyses, SampleInformation, SampleInformationDetails, SampleInformationDetailsSeparator, SampleInformationLabel, SampleInformationSeparator, SaturatedProperties, WellInformation } from '../../models/shared';
import { RecombinationQCReportData } from '../../recombination-qc/models/recombination-qc';
import { DecontaminationReportData } from '../../liveoil-decontamination/models/decontamination';
import { reportConstants, reportConstantsMessage } from '../../common/constants';
import { SwellingFinalReport, Vw_SwellReportData } from '../../swell/models/swelling';
import { ProjectService } from '../project.service';
import { FractionReports, SampleReport, SeparatorGasReports, SeparatorOilProcessModel, SeparatorOilReports } from '../../models/project';
import { FinalReportDetailsModel } from '../../models/sample';
import { ReportGeneralUtilityService } from '../report.generalUtility.service';
import { ReportPDFDiffLibRenderService } from './report.pdfDiffLibRender.service';
import { ReportPDFCceRenderService } from './report.pdfCceRender.service';
import { ReportPDFCompositionRenderService } from './report.pdfCompositionRender.service';
import { ReportPDFCvdRenderService } from './report.pdfCvdRender.service';
import { ReportPDFViscosityRenderService } from './report.pdfViscosityRender.service';
import { ReportPDFMsstRenderService } from './report.pdfMsstRender.service';
import { ReportPDFSeparatorGasRenderService } from './report.pdfSeparatorGasRender.service';
import { ReportPDFSeparatorOilRenderService } from './report.pdfSeparatorOilRender.service';
import { ReportPDFParaffinRenderService } from './report.pdfParaffinRender.service';
import { ReportPDFRecombinationQcRenderService } from './report.pdfRecombinationQcRender.service';
import { ReportPDFDecontaminationRenderService } from './report.pdfDecontaminationRender.service';
import { ReportPDFSwellingRenderService } from './report.pdfSwellingRender.service';
import { ReportPDFWorkbookRenderService } from './report.pdfWorkbookRender.service';
import { setNumericOrTextCellValue } from './report.cellFormat.util';
@Injectable({
    providedIn: 'root'
})
export class ReportExcelBuildersService implements OnDestroy {
    private projectService = inject(ProjectService);
    private _reportGeneralUtilityService = inject(ReportGeneralUtilityService);
    private reportPDFDiffLibRenderService = inject(ReportPDFDiffLibRenderService);
    private reportPDFCceRenderService = inject(ReportPDFCceRenderService);
    private reportPDFCompositionRenderService = inject(ReportPDFCompositionRenderService);
    private reportPDFCvdRenderService = inject(ReportPDFCvdRenderService);
    private reportPDFViscosityRenderService = inject(ReportPDFViscosityRenderService);


    private reportPDFMsstRenderService = inject(ReportPDFMsstRenderService);
    private reportPDFSeparatorGasRenderService = inject(ReportPDFSeparatorGasRenderService);
    private reportPDFSeparatorOilRenderService = inject(ReportPDFSeparatorOilRenderService);
    private reportPDFParaffinRenderService = inject(ReportPDFParaffinRenderService);
    private reportPDFRecombinationQcRenderService = inject(ReportPDFRecombinationQcRenderService);
    private reportPDFDecontaminationRenderService = inject(ReportPDFDecontaminationRenderService);
    private reportPDFSwellingRenderService = inject(ReportPDFSwellingRenderService);
    private reportPDFWorkbookRenderService = inject(ReportPDFWorkbookRenderService);
    private readonly destroy$ = new Subject<void>();
    async GenerateExcel(labTestData: LabTestReportData, reportType: string) {
        if (labTestData !== undefined) {

            const bcaExp: BCAReports[] = labTestData.expResults.filter((x): x is BCAReports => x.experimentName === reportConstants.experimentNameBCA);
            const msstExp: MSSTReportData[] = labTestData.expResults.filter((x): x is MSSTReportData => x.experimentName === reportConstants.experimentNameMSST);
            const cvdExp: CVDReportData[] = labTestData.expResults.filter((x): x is CVDReportData => x.experimentName === reportConstants.experimentNameCVD);
            const paraffinExp: ParaffinReports[] = labTestData.expResults.filter((x): x is ParaffinReports => x.experimentName === reportConstants.experimentNameParaffin) ?? null;
            const viscosityExps: viscosityReports[] = labTestData.expResults.filter((x): x is viscosityReports => x.experimentName === reportConstants.experimentNameViscosity);
            const fractionExp: FractionReports = labTestData.expResults.filter((x): x is FractionReports => x.experimentName === reportConstants.experimentNameFraction)[0] ?? null;
            const recombinationQC: RecombinationQCReportData[] = labTestData.expResults.filter((x): x is RecombinationQCReportData => x.experimentName === reportConstants.experimentNameRecombination) ?? null;
            const decontamination: DecontaminationReportData[] = labTestData.expResults.filter((x): x is DecontaminationReportData => x.experimentName === reportConstants.experimentNameDecontamination) ?? null;
            const summaryOfCCE = labTestData.expResults.filter((x): x is SummaryOfCCEReports => x.experimentName === reportConstants.experimentNameSummaryOfCCE) ?? null;
            const summaryOfCCEDew = labTestData.expResults.filter((x): x is SummaryOfCCEReports => x.experimentName === reportConstants.experimentNameSummaryOfCCEDewPoint) ?? null;
            const swellingExp = labTestData.expResults.filter((x): x is SwellingFinalReport => x.experimentName === reportConstants.experimentNameSwelling);
            const dleExp: DLEReportData[] = labTestData.expResults.filter((x): x is DLEReportData => x.experimentName === reportConstants.experimentNameDLE);
            const sampleDetailsWrap: FractionUpdate | null = labTestData.expResults.find((x): x is FractionUpdate => x.experimentName === reportConstants.experimentNameSampleDetails) ?? null;
            const sampleDetailsExp: SampleReport = sampleDetailsWrap?.data ?? {} as SampleReport;
            const reportContent = this._reportGeneralUtilityService.SelectReportContent(reportType, labTestData, sampleDetailsExp, sampleDetailsExp.finalReportDetails);
            const wellName = reportContent?.wellInformation.well != undefined ? reportContent.wellInformation.well : '';
            const sampleId = reportContent?.sampleInformationDetails[0]?.sampleId != undefined ? reportContent.sampleInformationDetails[0].sampleId : '';
            const separatorGasComp: SeparatorGasReports[] = labTestData.expResults.filter((x): x is SeparatorGasReports => x.experimentName === reportConstants.experimentNameSeparatorGas);
            const separatorOilComp: SeparatorOilReports[] = labTestData.expResults.filter((x): x is SeparatorOilReports => x.experimentName === reportConstants.experimentNameSeparatorOil);
            const listOfCCE = labTestData.expResults.filter((x): x is CCEReports => x.experimentName === reportConstants.experimentNameCCE);
            const listOfCCEDewPoint = labTestData.expResults.filter((x): x is CCEDewPointReports => x.experimentName === reportConstants.experimentNameCCEDewPoint);

            const workbook = new Workbook();

            const labTestId = labTestData.dataLabTest[0]?.labTestId;

            if (!labTestId) {
                console.error(reportConstantsMessage.errorMessageForLabTestId);
                return;
            }

            /*right side sub header*/
            let subHeader = "";
            const rowsFinal: FinalReportDetailsModel[] = Array.isArray(sampleDetailsExp.finalReportDetails) ? sampleDetailsExp.finalReportDetails : [sampleDetailsExp.finalReportDetails];
            const newSampleInfoReport = rowsFinal.at(0) ?? null;
            if (reportType === reportConstants.reportTypeDST || reportType === reportConstants.reportTypeMDT) {
                subHeader = reportContent?.wellInformation?.well + " " + reportType.toUpperCase() + " " + reportConstants.samples + " (" + reportConstants.perforationInterval + ": " + (newSampleInfoReport?.productionInterval ?? "") + ")";
            }
            else {
                subHeader = reportContent?.wellInformation?.well + " " + reportContent?.wellInformation?.formation + " (" + reportConstants.productionInterval + ": " + (newSampleInfoReport?.productionInterval ?? "") + ")";
            }


            const meta = this._reportGeneralUtilityService.GetFinalReportNamesNew(sampleDetailsExp.finalReportDetails);

            await this.GenerateFrontPage(workbook, reportContent.wellDetails, meta.frontPageNames);

            const addressToName = meta.reportToPrefix ? `${meta.reportToPrefix}. ${meta.toName}` : meta.toName; // Address line:
            const dearToName = meta.toName; // Dear line: (NO PREFIX)

            await this.GenerateCoverLetter(
                workbook,
                reportContent.coverData,
                addressToName,
                meta.fromName,
                reportContent.coverContent,
                reportType.toLowerCase() === reportConstants.reportTypeSeparatorTest ? subHeader : '',
                dearToName,
                sampleDetailsExp.finalReportDetails
            );
            const tableOfContentWorksheet = workbook.addWorksheet(reportConstants.tableOfContent);
            await this.GenerateCenteredSection(workbook, reportConstants.analysesMethods2, subHeader, reportConstants.summaryOfAnalysesMethods);
            let index = reportContent.tableItem.findIndex(x => x.title == reportConstants.summaryOfAnalysisMethods);
            if (index > -1) {
                reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
            } else {
                const index = reportContent.tableItem.findIndex(x => x.title == reportConstants.summaryOfAnalysesMethods);
                if (index > -1) {
                    reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                }
            }
            await this.GenerateAnalysesMethods(workbook, reportContent.sampleAnalyses, subHeader);

            /*Compositional Analysis*/
            if (reportType.toLowerCase() == reportConstants.reportTypeMDT || reportType.toLowerCase() == reportConstants.reportTypeDST) {
                const tableItems = reportContent.tableItem;
                await this.GenerateCenteredSection(workbook, reportConstants.sampleInfo2, subHeader, reportConstants.reservoirWellAndSamplingInformation);
                index = reportContent.tableItem.findIndex(x => x.title.includes(reportConstants.samplingInformation));
                if (index > -1) {
                    reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                }
                await this.GenerateSampleInfo(workbook, ' ' + reportConstants.sampleInfo, reportContent.wellInformation, reportContent.sampleInformation, reportContent.sampleInformationSeparator, reportContent.sampleInformationDetails, reportContent.reservoirProperties, reportContent.reservoirPropertiesDST, reportContent.saturatedProperties, reportContent.sampleInformationDetailsSeparator, reportContent.reservoirPropertiesSeparator, reportType, subHeader);

                let subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleId;

                const sampleList = sampleDetailsExp.samplingInformation?.rows ?? [];

                sampleList.forEach((sampleElement) => {

                    const bcaExp: BCAReports[] = labTestData.expResults.filter((x): x is BCAReports => x.experimentName === reportConstants.experimentNameBCA && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase()) ?? null;
                    const decontamination = labTestData.expResults.filter((x): x is DecontaminationReportData => x.experimentName === reportConstants.experimentNameDecontamination && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase()) ?? null;
                    const paraffinExp = labTestData.expResults.filter((x): x is ParaffinReports => x.experimentName === reportConstants.experimentNameParaffin && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase()) ?? null;
                    const recombinationQC = labTestData.expResults.filter((x): x is RecombinationQCReportData => x.experimentName === reportConstants.experimentNameRecombination && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase()) ?? null;

                    subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                    /*Compositional Analysis*/

                    if (bcaExp.length > 0 || recombinationQC.length > 0 || decontamination.length > 0 || paraffinExp.length > 0) {
                        /*BCA*/
                        const sheetName = reportType.toLowerCase() == reportConstants.reportTypeSeparatorTest ? reportConstants.compAnalysis : reportConstants.singleStageFlash + sampleElement.sampleNumber;
                        this.GenerateCenteredSection(workbook, sheetName, subHeader, reportConstants.compositionalAnalysis, reportType == reportConstants.reportTypeSeparatorTest ? "" : subContent);

                        const pageBCA = reportContent.wellInformation.well + ' ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + " (" + sampleElement.sampleDepthPoint + " " + reportConstants.mMD + ")";
                        const findBCASummaryIndex = reportContent.tableItem.findIndex(x => x.title === reportConstants.compositionalAnalysis);
                        const index = reportContent.tableItem.findIndex(
                            (x, i) => i > findBCASummaryIndex && x.title === pageBCA
                        );
                        if (index > -1) {
                            reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                        }
                        /*BCA*/
                        (bcaExp.map(element => {
                            if (element.bcaData.bcaMeasuredCompositionFlash.isRecombined == false) {
                                const sampleV = reportType.toUpperCase() + ' ' + reportConstants.sample + ' ' + sampleElement.sampleNumber;
                                let base = reportConstants.compAnalysis1 + sampleElement.sampleNumber;
                                let sheetName = this.GetUniqueSheetName(workbook, base);
                                this.GenerateCompositionalAnalysis(workbook, sheetName, element, wellName, sampleV, subHeader);
                                base = reportConstants.compAnalysis2 + sampleElement.sampleNumber;
                                sheetName = this.GetUniqueSheetName(workbook, base);
                                this.GenerateSingleStageFlashGORData(workbook, sheetName, element, wellName, sampleV, subHeader);
                                base = reportConstants.compAnalysis3 + sampleElement.sampleNumber;
                                sheetName = this.GetUniqueSheetName(workbook, base);
                                this.GenerateMaterialBalanceCheckAndHoffmanPlot(workbook, sheetName, element, wellName, sampleV, subHeader);
                            }
                        }));
                        /*n-Paraffin Analysis*/
                        if (paraffinExp.length > 0) {
                            (paraffinExp.map(element => {
                                const sampleV = reportType.toUpperCase() + ' ' + reportConstants.sample + ' ' + sampleElement.sampleNumber;
                                const base = reportConstants.experimentNameParaffin.toLowerCase() + '-' + sampleElement.sampleNumber;
                                const sheetName = this.GetUniqueSheetName(workbook, base);
                                this.GenerateNParaffinAnalysis(workbook, sheetName, element, wellName + '', sampleV, subHeader);
                            }));
                        }
                        /*LiveOil_Decontamination*/
                        (decontamination.map(decontExp => {
                            const sampleV = reportType.toUpperCase() + ' ' + reportConstants.sample + ' ' + sampleElement.sampleNumber;
                            let base = reportConstants.decontaminatedComp + sampleElement.sampleNumber;
                            let sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateDecontamination(workbook, sheetName, decontExp, wellName, sampleV, subHeader);
                            base = reportConstants.decontaminatedComp2 + sampleElement.sampleNumber;
                            sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateDecontaminationComp(workbook, sheetName, decontExp, wellName, sampleV, subHeader);
                        }));
                        /*Recombined BCA*/
                        (bcaExp.map(element => {
                            if (element.bcaData.bcaMeasuredCompositionFlash.isRecombined == true) {
                                const sampleV = reportType.toUpperCase() + ' ' + reportConstants.sample + ' ' + sampleElement.sampleNumber;
                                let base = reportConstants.recombinedOilCompAnalysis + sampleElement.sampleNumber;
                                let sheetName = this.GetUniqueSheetName(workbook, base);
                                this.GenerateCompositionalAnalysis(workbook, sheetName, element, wellName, sampleV, subHeader,reportConstants.recombinedFluid);
                                base = reportConstants.recombinedOilCompAnalysis1 + sampleElement.sampleNumber;
                                sheetName = this.GetUniqueSheetName(workbook, base);
                                this.GenerateSingleStageFlashGORData(workbook, sheetName, element, wellName, sampleV, subHeader,reportConstants.recombinedFluid);
                                base = reportConstants.recombinedOilCompAnalysis2 + sampleElement.sampleNumber;
                                sheetName = this.GetUniqueSheetName(workbook, base);
                                this.GenerateMaterialBalanceCheckAndHoffmanPlot(workbook, sheetName, element, wellName, sampleV, subHeader,reportConstants.recombinedFluid);
                            }
                        }));
                        /*RC QC*/
                        (recombinationQC.map(recombExp => {
                            const sampleV = reportType.toUpperCase() + ' ' + reportConstants.sample + ' ' + sampleElement.sampleNumber;
                            const base = reportConstants.calcWellstream + sampleElement.sampleNumber;
                            const sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateRecombinationQC(workbook, sheetName, recombExp, wellName, sampleV, subHeader);
                        }));


                    }
                });
                /*Summary of CCE Experiments*/
                sampleList.forEach((sampleElement) => {
                    const summaryOfCCE = labTestData.expResults.filter((x): x is SummaryOfCCEReports => x.experimentName === reportConstants.experimentNameSummaryOfCCE && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase()) ?? null;
                    const summaryOfCCEDew = labTestData.expResults.filter((x): x is SummaryOfCCEReports => x.experimentName === reportConstants.experimentNameSummaryOfCCEDewPoint && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase()) ?? null;

                    if (summaryOfCCE.length > 0 || summaryOfCCEDew.length > 0) {
                        const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                        this.GenerateCenteredSection(workbook, reportConstants.experimentNameCCE + '-' + sampleElement.sampleNumber, subHeader, reportConstants.summaryOfConstantCompositionExpansionExperiments, subContent, 20, 16);

                        const pageCCE = reportContent.wellInformation.well + ' ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + " (" + sampleElement.sampleDepthPoint + " " + reportConstants.mMD + ")"
                        const findCCESummaryIndex = reportContent.tableItem.findIndex(x => x.title === reportConstants.summaryOfCCEExperiments);
                        const index = reportContent.tableItem.findIndex(
                            (x, i) => i > findCCESummaryIndex && x.title === pageCCE
                        );

                        if (index > -1) {
                            reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                        }
                        const sampleV = reportType.toUpperCase() + ' ' + reportConstants.sample + ' ' + sampleElement.sampleNumber;
                        summaryOfCCE.map(exp => {
                            const base = reportConstants.lzPhaseEnvelop + sampleElement.sampleNumber;
                            const sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateSummaryOfCCE(workbook, sheetName, exp, wellName, sampleV, '', '', reportType, subHeader);
                        });
                        summaryOfCCEDew.map(exp => {
                            const base = reportConstants.lzPhaseEnvelop + sampleElement.sampleNumber;
                            const sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateSummaryOfCCE(workbook, sheetName, exp, wellName, sampleV, '', '', reportType, subHeader);
                        });
                    }
                });

                /*Separator Test*/
                sampleList.forEach((sampleElement) => {
                    const msstExp: MSSTReportData[] = labTestData.expResults.filter((x): x is MSSTReportData => x.experimentName === reportConstants.experimentNameMSST && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase());
                    const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                    if (msstExp.length > 0) {
                        this.GenerateCenteredSection(workbook, ' ' + reportConstants.sepTest + sampleElement.sampleNumber, subHeader, reportConstants.separatorTest, subContent, 24, 16);
                        index = reportContent.tableItem.findIndex(x => x.title === reportConstants.separatorTests + ' (' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ')');
                        if (index > -1) {
                            reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                        }
                        const sampleV = reportType.toUpperCase() + ' ' + reportConstants.sample + ' ' + sampleElement.sampleNumber;
                        (msstExp.map(element => {
                            const base = reportConstants.separatorTest + '-' + sampleElement.sampleNumber;
                            const sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateSeparatorTest(workbook, sheetName, element, wellName, sampleV, subHeader, '', reportType == reportConstants.reportTypeDST ? true : false);
                        }));
                    }
                });
                /*Differential Liberation*/
                sampleList.forEach((sampleElement) => {
                    const dleExp: DLEReportData[] = labTestData.expResults.filter((x): x is DLEReportData => x.experimentName === reportConstants.experimentNameDLE && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase());
                    const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                    if (dleExp.length > 0) {
                        this.GenerateCenteredSection(workbook, reportConstants.differentialLiberation + '-' + sampleElement.sampleNumber, subHeader, reportConstants.differentialLiberation, subContent, 24, 16);
                        index = reportContent.tableItem.findIndex(x => x.title === reportConstants.differentialLiberation + ' (' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ')');
                        if (index > -1) {
                            reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                        }
                        const sampleV = reportType.toUpperCase() + ' ' + reportConstants.sample + ' ' + sampleElement.sampleNumber;
                        dleExp.map(element => {
                            const base = reportConstants.diffLib + '-' + sampleElement.sampleNumber;
                            const sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateDiffLibTest(workbook, sheetName, element, subHeader, sampleV);
                        });
                    }
                });
                /*Constant Volume Depletion (CVD)*/
                sampleList.forEach((sampleElement) => {
                    const cvdExp: CVDReportData[] = labTestData.expResults.filter((x): x is CVDReportData => x.experimentName === reportConstants.experimentNameCVD && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase());
                    if (cvdExp.length > 0) {
                        const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                        this.GenerateCenteredSection(workbook, reportConstants.e_CVD + sampleElement.sampleNumber, subHeader, reportConstants.constantVolumeDepletion, subContent, 20, 16);
                        index = reportContent.tableItem.findIndex(x => x.title === reportConstants.cvdExperiment + ' (' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ')');
                        if (index > -1) {
                            reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                        }
                        const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                        cvdExp.map(async element => {
                            const base = reportConstants.experimentNameCVD + '-' + sampleElement.sampleNumber;
                            const sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateCVD(workbook, sheetName, element, reportType == reportConstants.reportTypeDST ? wellName + ' ' + reportConstants.dstSample : wellName, sampleV, subHeader);
                        });
                    }
                });

                /*Viscosity*/
                sampleList.forEach((sampleElement) => {
                    const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                    const viscosityExps: viscosityReports[] = labTestData.expResults.filter((x): x is viscosityReports => x.experimentName === reportConstants.experimentNameViscosity
                        && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase()
                    );
                    if (viscosityExps.length > 0) {
                        this.GenerateCenteredSection(workbook, reportConstants.experimentNameViscosity + '-' + sampleElement.sampleNumber, subHeader, reportConstants.experimentNameViscosity, subContent, 24, 16);
                        const pageViscosity = reportContent.wellInformation.well + ' ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + " (" + sampleElement.sampleDepthPoint + " " + reportConstants.mMD + ")"
                        const findViscositySummaryIndex = reportContent.tableItem.findIndex(x => x.title === reportConstants.experimentNameViscosity);
                        const index = reportContent.tableItem.findIndex(
                            (x, i) => i > findViscositySummaryIndex && x.title === pageViscosity
                        );
                        if (index > -1) {
                            reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                        }
                        const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                        viscosityExps.map(async element => {
                            const base = reportConstants.visc + sampleElement.sampleNumber + element.viscosityData[0].temperature + reportConstants.f;
                            const sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateViscosity(workbook, sheetName, element, wellName, sampleV, subHeader);
                        });
                    }
                });

                /*Swelling*/
                sampleList.forEach((sampleElement) => {
                    const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                    const swellingExp = labTestData.expResults.filter((x): x is SwellingFinalReport => x.experimentName === reportConstants.experimentNameSwelling && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase()) ?? null;
                    if (swellingExp.length > 0) {

                        this.GenerateCenteredSection(workbook, reportConstants.swellingTest + sampleElement.sampleNumber, subHeader, reportConstants.summaryOfSwellingTestExperiments, subContent, 24, 16);

                        const pageSwelling = reportContent.wellInformation.well + ' ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + " (" + sampleElement.sampleDepthPoint + " " + reportConstants.mMD + ")"
                        const findSwellingSummaryIndex = reportContent.tableItem.findIndex(x => x.title === reportConstants.summaryOfSwellingTest);
                        const index = reportContent.tableItem.findIndex(
                            (x, i) => i > findSwellingSummaryIndex && x.title === pageSwelling
                        );
                        if (index > -1) {
                            reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                        }
                        const batches = swellingExp.map((_, i) => {
                            const base = reportConstants.swellReportSummary + sampleElement.sampleNumber;
                            return {
                                main: base,
                                gas: `${base}${reportConstants.swellReportGas} ${i + 1}`,
                                calc: `${base}${reportConstants.swellReportCalc} ${i + 1}`
                            };
                        });

                        swellingExp.map((swellExp, i) => {
                            const names = batches[i];
                            const sheetNameMain = this.GetUniqueSheetName(workbook, names.main);
                            const sheetNameGas = this.GetUniqueSheetName(workbook, names.gas);
                            const sheetNameCalc = this.GetUniqueSheetName(workbook, names.calc);
                            this.GenerateSwellingReport(workbook, sheetNameMain, swellExp, wellName, subHeader);

                            this.GenerateSwellingGasCompDataReport(workbook, sheetNameGas, swellExp, wellName, subHeader);
                            this.GenerateSwellingCalculatedCompBalData(workbook, sheetNameCalc, swellExp, subHeader);
                        });
                    }
                });
                // Add summary of swelling test call here with null check

                /*Appendix*/

                /*Appendix Fraction Reference Values*/

                if (fractionExp.components.length > 0) {
                    const content = tableItems.filter(x => x.experimentName == reportConstants.experimentNameFraction);
                    const letter = this.ExtractAppendixCode(content[0].title);
                    await this.GenerateCenteredSection(workbook, reportConstants.app + letter, subHeader, content[0].title, subContent, 20, 16);

                    index = reportContent.tableItem.findIndex(x => x.title.includes(content[0].title));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                    }
                    await this.GenerateFractionAppendix(workbook, reportConstants.app + letter + ' ' + reportConstants.MW_SG, fractionExp, subHeader);
                }
                /*Appendix CCE Data*/
                const appendixData = reportContent.tableItem
                    .map(x => { return x })
                    .filter(x => x.title.includes(reportConstants.appendix));

                sampleList.forEach((sampleElement) => {
                    const summaryOfCCE = labTestData.expResults.filter((x): x is SummaryOfCCEReports => x.experimentName === reportConstants.experimentNameSummaryOfCCE && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase()) ?? null;
                    const summaryOfCCEDew = labTestData.expResults.filter((x): x is SummaryOfCCEReports => x.experimentName === reportConstants.experimentNameSummaryOfCCEDewPoint && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase()) ?? null;
                    const listOfCCE = labTestData.expResults.filter((x): x is CCEReports => x.experimentName === reportConstants.experimentNameCCE && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase());
                    const listOfCCEDewPoint: CCEDewPointReports[] = labTestData.expResults.filter((x): x is CCEDewPointReports => x.experimentName === reportConstants.experimentNameCCEDewPoint && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase());
                    const countOfCCEExp = appendixData.filter(x => x.experimentName == reportConstants.experimentNameCCE);

                    if (summaryOfCCE.length > 0 || summaryOfCCEDew.length > 0) {
                        const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                        const appendixCCE = countOfCCEExp.find(x => x.title.includes(sampleElement.sampleNumber ?? ""));

                        const letter = this.ExtractAppendixCode(appendixCCE?.title ?? "");
                        this.GenerateCenteredSection(workbook, reportConstants.app + letter, subHeader, (appendixCCE?.title ?? "").replace(new RegExp(`(${reportConstants.CCEData})`), reportConstants.dollar1), subContent, 20, 16);
                        index = reportContent.tableItem.findIndex(x => x.title.includes(appendixCCE?.title ?? ""));
                        if (index > -1) {
                            reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                        }
                        /*Summary of CCE */
                        const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                        summaryOfCCE.map(exp => {
                            const base = reportConstants.app + letter + ' ' + reportConstants.CCEData + '-' + sampleElement.sampleNumber;
                            const sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateSummaryOfCCE(workbook, sheetName, exp, wellName, sampleV, reportConstants.appendix, letter != null ? letter : '', reportType, subHeader);
                        });

                        listOfCCE.map(exp => {
                            if (exp.cceProperties.isSwelling == false) {
                                const base = reportConstants.experimentNameCCE + '-' + sampleElement.sampleNumber + ' ' + exp.cceProperties.studyTemperature + reportConstants.f;
                                const sheetName = this.GetUniqueSheetName(workbook, base);
                                this.GenerateCCE(workbook, sheetName, exp, wellName, sampleV, subHeader);
                                const sheetNamePlot = this.GetUniqueSheetName(workbook, base + reportConstants.plots);
                                this.GenerateCCEPlots(workbook, sheetNamePlot, exp, wellName, sampleV, subHeader);
                            }
                        });
                        /****************************************************************/
                        /*Summary Of CCE DewPoint */
                        summaryOfCCEDew.map(exp => {
                            const base = reportConstants.app + letter + ' ' + reportConstants.CCEData + '-' + sampleElement.sampleNumber;
                            const sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateSummaryOfCCE(workbook, sheetName, exp, wellName, sampleV, reportConstants.appendix, letter != null ? letter : '', reportType, subHeader);
                        });

                        listOfCCEDewPoint.map(exp => {
                            if (exp.cceDewPointProperties.isSwelling == false) {
                                const base = reportConstants.experimentNameCCE + '-' + sampleElement.sampleNumber + ' ' + exp.cceDewPointProperties.studyTemperature + reportConstants.f;
                                const sheetName = this.GetUniqueSheetName(workbook, base);
                                this.GenerateCCEDewPoint(workbook, sheetName, exp, wellName + reportType.toUpperCase() + ' ' + reportConstants.sample, sampleV, subHeader);
                                const sheetNamePlot = this.GetUniqueSheetName(workbook, base + reportConstants.plots);
                                this.GenerateCCEDewPlots(workbook, sheetNamePlot, exp, wellName, sampleV, subHeader);
                            }
                        });
                    }
                });

                /*Appendix Swelling*/
                sampleList.forEach((sampleElement) => {

                    const swellingExp = labTestData.expResults.filter((x): x is SwellingFinalReport => x.experimentName === reportConstants.experimentNameSwelling
                        && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase()
                    ) ?? null;
                    const listOfCCE = labTestData.expResults.filter((x): x is CCEReports => x.experimentName === reportConstants.experimentNameCCE
                        && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase()
                    );

                    const countOfSSwellingExp = appendixData.filter(x => x.experimentName === reportConstants.experimentNameSwelling);
                    if (swellingExp.length > 0) {
                        const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                        const appendixSwell = countOfSSwellingExp.find(x => x.title.includes(sampleElement.sampleNumber ?? ""));
                        const letter = this.ExtractAppendixCode(appendixSwell?.title ?? "");
                        this.GenerateCenteredSection(workbook, reportConstants.appendix + ' ' + letter, subHeader, reportConstants.appendix + ' ' + letter + reportConstants.swellingTestSepGasData, subContent, 24, 16);
                        index = reportContent.tableItem.findIndex(x => x.title.includes(appendixSwell?.title ?? ""));
                        if (index > -1) {
                            reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                        }
                        const batches = swellingExp.map((_, i) => {
                            const base = reportConstants.swellReport + sampleElement.sampleNumber;
                            return {
                                main: base,
                                gas: `${base}${reportConstants.swellReportGas} ${i + 1}`,
                                calc: `${base}${reportConstants.swellReportCalc} ${i + 1}`
                            };
                        });
                        const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                        swellingExp.map((swellExp, i) => {
                            const names = batches[i];
                            const sheetNameMain = reportConstants.app + letter + ' ' + this.GetUniqueSheetName(workbook, names.main);
                            this.GenerateSwellingReport(workbook, sheetNameMain, swellExp, wellName, subHeader, reportConstants.appendix, letter != null ? letter : '');


                            //this foreach will change based on mol% and temp
                            listOfCCE.map(exp => {
                                if (exp.cceProperties.isSwelling === true && exp.cceProperties.swellingSummaryId === swellExp.labRepeatId) {

                                    const cceSatP = exp.bpTable.find((x: BPTable) => x.pointName === reportConstants.satP)?.pressure;
                                    const temp = exp.cceProperties.studyTemperature;
                                    const swellingSatP = (swellExp.swellReport.find((x: Vw_SwellReportData) => x.temperature === temp && x.saturationPressure === cceSatP)?.moleFracInjGas ?? 0) * 100;

                                    const molPercentage = parseFloat(swellingSatP.toFixed(2));
                                    const base = reportConstants.experimentNameCCE + sampleElement.sampleNumber + ' ' + molPercentage + '% ' + exp.cceProperties.studyTemperature + reportConstants.fInjG;
                                    const sheetName = this.GetUniqueSheetName(workbook, base);
                                    this.GenerateCCESepGas(workbook, sheetName, exp, wellName, molPercentage, subHeader);
                                    const plotBase = reportConstants.experimentNameCCE + reportConstants.plots + sampleElement.sampleNumber + ' ' + molPercentage + '% ' + exp.cceProperties.studyTemperature + reportConstants.fInjG;
                                    const sheetNamePlot = this.GetUniqueSheetName(workbook, plotBase);
                                    this.GenerateCCEGasPlots(workbook, sheetNamePlot, exp, wellName, molPercentage, subHeader);
                                }
                            });

                            listOfCCEDewPoint.map(exp => {
                                if (exp.cceDewPointProperties.isSwelling === true && exp.cceDewPointProperties.swellingSummaryId === swellExp.labRepeatId) {

                                    const cceSatP = exp.dpTable.find((x: DPTable) => x.pointName === reportConstants.pSat)?.pressure;
                                    const temp = exp.cceDewPointProperties.studyTemperature;
                                    const swellingSatP = (swellExp.swellReport.find((x: Vw_SwellReportData) => x.temperature === temp && x.saturationPressure === cceSatP)?.moleFracInjGas ?? 0) * 100;

                                    const molPercentage = parseFloat(swellingSatP.toFixed(2));
                                    const base = reportConstants.swellCCEDew + sampleElement.sampleNumber + ' ' + molPercentage + '% ' + exp.cceDewPointProperties.studyTemperature + reportConstants.fInjG;
                                    const sheetName = this.GetUniqueSheetName(workbook, base);
                                    this.GenerateCCEDewPointSepGas(workbook, sheetName, exp, wellName, molPercentage, subHeader);
                                    const plotBase = reportConstants.cceDew + reportConstants.plots + sampleElement.sampleNumber + ' ' + molPercentage + '% ' + exp.cceDewPointProperties.studyTemperature + reportConstants.fInjG;
                                    const sheetNamePlot = this.GetUniqueSheetName(workbook, plotBase);
                                    this.GenerateCCEDewPointGasPlots(workbook, sheetNamePlot, exp, wellName, sampleV, molPercentage, subHeader);
                                }
                            });

                        });
                    }
                });

                /*Appendix MSST*/
                try {
                    sampleList.forEach((sampleElement) => {
                        const msstExp: MSSTReportData[] = labTestData.expResults.filter((x): x is MSSTReportData => x.experimentName === reportConstants.experimentNameMSST
                            && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase()
                        );
                        const countOfMSSTExp = appendixData.filter(x => x.experimentName == reportConstants.experimentNameMSST);
                        if (msstExp.length > 0) {

                            const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                            const appendixMSST = countOfMSSTExp.find(x => x.title.includes(sampleElement.sampleNumber ?? ""));
                            const letter = this.ExtractAppendixCode(appendixMSST?.title ?? "");

                            this.GenerateCenteredSection(workbook, reportConstants.appendix + ' ' + letter, subHeader, reportConstants.appendix + ' ' + letter + ':' + (reportType == reportConstants.reportTypeDST ? reportConstants.separatorTest : reportConstants.separatorTestsMaterialBalance), subContent, 24, 16);
                            index = reportContent.tableItem.findIndex(x => x.title.includes(appendixMSST?.title ?? ""));
                            if (index > -1) {
                                reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                            }
                            const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                            (msstExp.map(element => {
                                let base = reportConstants.separatorTest + '-' + sampleElement.sampleNumber;
                                const sheetName = this.GetUniqueSheetName(workbook, base);
                                this.GenerateSeparatorTest(workbook, sheetName, element, wellName, sampleV, subHeader, reportConstants.appendix + ' ' + letter + ': ', true);
                                base = reportConstants.sepTestVaporStage + '-' + sampleElement.sampleNumber;
                                const sheetName1 = this.GetUniqueSheetName(workbook, base);
                                this.GenerateSeparatorTestVaporCompositions(workbook, sheetName1, element, subHeader);
                                base = reportConstants.stresidualOilComp + '-' + sampleElement.sampleNumber;
                                const sheetName2 = this.GetUniqueSheetName(workbook, base);
                                this.GenerateSeparatorTestResidualOilComp(workbook, sheetName2, element, subHeader);
                                base = reportConstants.materialBalanceSummary + '-' + sampleElement.sampleNumber;
                                const sheetName3 = this.GetUniqueSheetName(workbook, base);
                                this.GenerateMaterialBalanceSummary(workbook, sheetName3, element, subHeader);
                            }));
                        }
                    });
                }
                catch (error) {
                    console.error(reportConstantsMessage.errorGeneratingAppendixMSST, error);
                }
                /*Appendix D-Diff Lib Material Balance*/
                sampleList.forEach((sampleElement) => {
                    const dleExp: DLEReportData[] = labTestData.expResults.filter((x): x is DLEReportData => x.experimentName === reportConstants.experimentNameDLE && x.sampleIdentifier?.toLowerCase() == sampleElement.sampleIdentifier?.toLowerCase());
                    const countOfDLEExp = appendixData.filter(x => x.experimentName == reportConstants.experimentNameDLE);
                    if (dleExp.length > 0) {
                        const subContent = reportContent.wellInformation.well + '  ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                        const appendixDL = countOfDLEExp.find(x => x.title.includes(sampleElement.sampleNumber ?? ""));
                        const letter = this.ExtractAppendixCode(appendixDL?.title ?? "");
                        this.GenerateCenteredSection(workbook, reportConstants.appendix + ' ' + letter, subHeader, reportConstants.appendix + ' ' + letter + '-' + reportConstants.diffLibMaterialBalance + '', subContent, 24, 16);

                        index = reportContent.tableItem.findIndex(x => x.title.includes(appendixDL?.title ?? ""));
                        if (index > -1) {
                            reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                        }
                        dleExp.map(element => {
                            let base = reportConstants.app + letter + ' ' + reportConstants.diffLib;
                            let sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateDiffLibTest(workbook, sheetName, element, subHeader, reportConstants.appendix + ' ' + letter + ': ');
                            base = reportConstants.dlVaporStageComps + sampleElement.sampleNumber;
                            sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateDiffLibTestVaporCompositions(workbook, sheetName, element, subHeader);
                            base = reportConstants.dlResidualOilComp + sampleElement.sampleNumber;
                            sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateDiffLibTestResidualOilComp(workbook, sheetName, element, subHeader);
                            base = reportConstants.materialBalanceSummaryWithSpace + sampleElement.sampleNumber;
                            sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateDiffLibMaterialBalanceSummary(workbook, sheetName, element, subHeader);
                        });
                    }
                });
                /*Appendix CVD*/
                sampleList.forEach((sampleElement) => {
                    const cvdExp: CVDReportData[] = labTestData.expResults.filter((x): x is CVDReportData => x.experimentName === reportConstants.experimentNameCVD
                        && x.sampleIdentifier?.toLowerCase() === sampleElement.sampleIdentifier?.toLowerCase()
                    );
                    const countOfCVDExp = appendixData.filter(x => x.experimentName === reportConstants.experimentNameCVD);
                    if (cvdExp.length > 0) {
                        const subContent = reportContent.wellInformation.well + ' ' + reportConstants.sample + ' ' + sampleElement.sampleNumber + ' (' + sampleElement.sampleDepthPoint + ' ' + reportConstants.mMD + ')';
                        const appendixCVD = countOfCVDExp.find(x => x.title.includes(sampleElement.sampleNumber ?? ""));
                        const letter = this.ExtractAppendixCode(appendixCVD?.title ?? "");
                        this.GenerateCenteredSection(workbook, reportConstants.appendix + ' ' + letter, subHeader, reportConstants.appendix + ' ' + letter + '- ' + reportConstants.constantVolumeDepletion, subContent, 24, 16);
                        index = reportContent.tableItem.findIndex(x => x.title.includes(appendixCVD?.title ?? ""));
                        if (index > -1) {
                            reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                        }
                        const sampleV = reportType.toUpperCase() + " " + reportConstants.sample + " " + sampleElement.sampleNumber;
                        (cvdExp.map(element => {
                            let base = reportConstants.app + letter + ' ' + reportConstants.cvdTest + sampleElement.sampleNumber;
                            let sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateCVD(workbook, sheetName, element, reportType === reportConstants.reportTypeDST ? wellName + ' ' + reportConstants.dstSample : wellName, sampleV, subHeader, reportConstants.appendix + ' ' + letter + ': ');
                            base = reportConstants.cvdVaporStageComps + sampleElement.sampleNumber;
                            sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateCVDVapor(workbook, sheetName, element, subHeader);
                            base = reportConstants.cvdLastStageOilComp + sampleElement.sampleNumber;
                            sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateCVDStageOil(workbook, sheetName, element, subHeader);
                            base = reportConstants.cvdMaterialBalanceSummary + sampleElement.sampleNumber;
                            sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateCVDMaterialBalance(workbook, sheetName, element, subHeader);
                        }));
                    }
                });


            }

            if (reportType.toLowerCase() === reportConstants.reportTypeSeparatorTest) {
                const samples = sampleDetailsExp.samplingInformation?.rows.filter(x => x.isRecombine == true);
                const recombinedSampleId = samples?.map(s => s.bottleNumber).join('-');
                await this.GenerateCenteredSection(workbook, reportConstants.sampleInfo2, subHeader, reportConstants.reservoirWellAndSamplingInformation);
                index = reportContent.tableItem.findIndex(x => x.title.includes(reportConstants.samplingInformation));
                if (index > -1) {
                    reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                }
                await this.GenerateSampleInfo(workbook, reportConstants.sampleInfo, reportContent.wellInformation, reportContent.sampleInformation, reportContent.sampleInformationSeparator, reportContent.sampleInformationDetails, reportContent.reservoirProperties, reportContent.reservoirPropertiesDST, reportContent.saturatedProperties, reportContent.sampleInformationDetailsSeparator, reportContent.reservoirPropertiesSeparator, reportType, subHeader);
                if (bcaExp.length > 0 || separatorOilComp.length > 0 || separatorGasComp.length > 0 || recombinationQC.length > 0 || decontamination.length > 0 || paraffinExp.length > 0) {
                    const sheetName = reportType.toLowerCase() === reportConstants.reportTypeSeparatorTest ? reportConstants.compAnalysis : reportConstants.singleStageFlash + sampleId;

                    await this.GenerateCenteredSection(workbook, sheetName, subHeader, reportConstants.compositionalAnalysis, '');

                    /*BCA*/
                    index = reportContent.tableItem.findIndex(x => x.title.includes(reportConstants.sampleCompositionalAnalysis));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                    }
                    const sampleName = reportConstants.experimentNameSeparatorOil;

                    if (separatorOilComp.length > 0) {
                        for (const exp of separatorOilComp) {
                            for (const template of exp.data) {
                                let base = reportConstants.sepOilCompAnalysis + "";
                                let sheetName = this.GetUniqueSheetName(workbook, base);
                                await this.GenerateSeparatorOilExcelReport(
                                    workbook,
                                    sheetName,
                                    template,
                                    wellName,
                                    sampleName,
                                    subHeader
                                );
                                base = reportConstants.sepOilCompAnalysis1;
                                sheetName = this.GetUniqueSheetName(workbook, base);
                                await this.GenerateSeparatorOilFlashGORData(
                                    workbook,
                                    sheetName,
                                    template,
                                    wellName,
                                    sampleName,
                                    subHeader
                                );
                                base = reportConstants.sepOilCompAnalysis2;
                                sheetName = this.GetUniqueSheetName(workbook, base);
                                await this.GenerateSeparatorOilMaterialBalanceHoffmanPlot(
                                    workbook,
                                    sheetName,
                                    exp.graphImages,
                                    wellName,
                                    sampleName,
                                    subHeader
                                );
                            }
                        }
                    }

                    /*n-Paraffin Analysis*/
                    if (paraffinExp.length > 0) {
                        /*n-Paraffin Analysis*/
                        paraffinExp.map(element => {
                            const base = reportConstants.nParaffin;
                            const sampleNumber = ' ' + (sampleDetailsExp?.rows?.find(x => x.isRecombine == true && (x.separatorOilGas == reportConstants.sepDeadOil || x.separatorOilGas == reportConstants.sepOil))?.bottleNumber ?? "");

                            const sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateNParaffinAnalysis(workbook, sheetName, element, wellName + ' ' + reportConstants.mdtSample, sampleNumber, subHeader);
                        });
                    }

                    if (separatorGasComp.length > 0) {
                        for (const exp of separatorGasComp) {
                            if (!Array.isArray(exp.data)) {
                                continue;
                            }
                            const base = reportConstants.sepGasCompAnalysis;
                            const sheetName = this.GetUniqueSheetName(workbook, base);
                            await this.GenerateSeparatorGasExcelReport(
                                workbook,
                                sheetName,
                                exp,
                                subHeader
                            );
                        }
                    }
                    /*BCA*/
                    (bcaExp.map(element => {
                        if (element.bcaData.bcaMeasuredCompositionFlash.isRecombined === false) {
                            let base = reportConstants.sepOilCompAnalysis + "";
                            let sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateCompositionalAnalysis(workbook, sheetName, element, wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader);
                            base = reportConstants.sepOilCompAnalysis1;
                            sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateSingleStageFlashGORData(workbook, sheetName, element, wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader);
                            base = reportConstants.sepOilCompAnalysis2;
                            sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateMaterialBalanceCheckAndHoffmanPlot(workbook, sheetName, element, wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader);
                        }
                    }));

                    /*Start Recombined Fluid*/
                    (bcaExp.map(element => {
                        if (element.bcaData.bcaMeasuredCompositionFlash.isRecombined === true) {
                            let base = reportConstants.recombinedOilCompAnalysis + "";
                            let sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateCompositionalAnalysis(workbook, sheetName, element, wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader,reportConstants.recombinedFluid);
                            base = reportConstants.recombinedOilCompAnalysis1;
                            sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateSingleStageFlashGORData(workbook, sheetName, element, wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader,reportConstants.recombinedFluid);
                            base = reportConstants.recombinedOilCompAnalysis2;
                            sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateMaterialBalanceCheckAndHoffmanPlot(workbook, sheetName, element, wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader,reportConstants.recombinedFluid);//reportConstants.recombinedFluid)
                        }
                    }));
                    /*end Recombined Fluid*/
                    /*RC QC*/
                    recombinationQC.map(recombExp => {
                        const base = reportConstants.calcWellstream;
                        const sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateRecombinationQC(workbook, sheetName, recombExp, wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader);
                    });
                    /*LiveOil_Decontamination*/
                    (decontamination.map(decontExp => {
                        let base = reportConstants.decontaminatedComp;
                        let sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateDecontamination(workbook, sheetName, decontExp, wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader);
                        base = reportConstants.decontaminatedComp2 + "";
                        sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateDecontaminationComp(workbook, sheetName, decontExp, wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader);
                    }));

                }
                /*Summary of CCE Experiments*/
                if (summaryOfCCE.length > 0 || summaryOfCCEDew.length > 0) {
                    await this.GenerateCenteredSection(workbook, reportConstants.experimentNameCCE, subHeader, reportConstants.summaryOfConstantCompositionExpansionExperiments, '', 20, 16);
                    index = reportContent.tableItem.findIndex(x => x.title.includes(reportConstants.summaryOfCCEExperiments));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                    }
                    summaryOfCCE.map(exp => {
                        const sheetName = reportConstants.lzPhaseEnvelop.replace('-', '');
                        this.GenerateSummaryOfCCE(workbook, sheetName, exp, wellName, reportConstants.sample + ' ' + recombinedSampleId, '', '', reportType, subHeader);
                    });
                    summaryOfCCEDew.map(exp => {
                        const sheetName = reportConstants.lzPhaseEnvelop + reportConstants.specificGravitySubscript;
                        this.GenerateSummaryOfCCE(workbook, sheetName, exp, wellName, reportConstants.sample + ' ' + recombinedSampleId, '', '', reportType, subHeader);
                    });

                }

                /*Separator Test*/
                if (msstExp.length > 0) {
                    await this.GenerateCenteredSection(workbook, reportConstants.sepTest1, subHeader, reportConstants.separatorTest, '', 24, 16);
                    index = reportContent.tableItem.findIndex(x => x.title.includes(reportConstants.separatorTest));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                    }
                    msstExp.map(element => {
                        const base = reportConstants.separatorTest;
                        const sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateSeparatorTest(workbook, sheetName, element, wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader);
                    });
                }

                if (dleExp.length > 0) {
                    /*Differential Liberation*/
                    await this.GenerateCenteredSection(workbook, reportConstants.differentialLiberation, subHeader, reportConstants.differentialLiberation, '', 24, 16);

                    index = reportContent.tableItem.findIndex(x => x.title.includes(reportConstants.differentialLiberation));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                    }
                    dleExp.map(element => {
                        const base = reportConstants.diffLib;
                        const sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateDiffLibTest(workbook, sheetName, element, subHeader, reportConstants.sample + ' ' + recombinedSampleId);
                    });
                }

                /*Constant Volume Depletion (CVD)*/
                if (cvdExp.length > 0) {
                    await this.GenerateCenteredSection(workbook, reportConstants.e_CVD, reportType === reportConstants.reportTypeDST ? wellName + ' ' + reportConstants.dstSample : subHeader, reportConstants.constantVolumeDepletion, '', 20, 16);
                    index = reportContent.tableItem.findIndex(x => x.title.includes(reportConstants.cvdExperiment));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                    }
                    cvdExp.map(element => {
                        const base = reportConstants.cvdExperiment;
                        const sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateCVD(workbook, sheetName, element, reportType === reportConstants.reportTypeDST ? wellName + ' ' + reportConstants.dstSample : wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader);
                    });
                }

                /*Viscosity*/
                if (viscosityExps.length > 0) {
                    await this.GenerateCenteredSection(workbook, reportConstants.experimentNameViscosity, subHeader, reportConstants.experimentNameViscosity, '', 24, 16);
                    index = reportContent.tableItem.findIndex(x => x.title.includes(reportConstants.experimentNameViscosity));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                    }
                    viscosityExps.map(element => {
                        const base = reportConstants.visc + element.viscosityData[0].temperature + reportConstants.f;
                        const sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateViscosity(workbook, sheetName, element, wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader);
                    });
                }

                /*Summary of Swelling Test Experiments (Sep Gas)*/
                if (swellingExp.length > 0) {
                    await this.GenerateCenteredSection(workbook, reportConstants.swellingTest.replace('-', ''), subHeader, reportConstants.summaryOfSwellingTestExperiments, '', 24, 16);
                    index = reportContent.tableItem.findIndex(x => x.title.includes(reportConstants.summaryOfSwellingTestSepGas));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                    }
                    const batches = swellingExp.map((_, i) => {
                        const base = reportConstants.swellReport;
                        return {
                            main: base,
                            gas: `${base} ${reportConstants.swellReportGas} ${i + 1}`,
                            calc: `${base}${reportConstants.swellReportCalc} ${i + 1}`
                        };
                    });
                    swellingExp.map((swellExp, i) => {
                        const names = batches[i];
                        const sheetNameMain = this.GetUniqueSheetName(workbook, names.main);
                        const sheetNameGas = this.GetUniqueSheetName(workbook, names.gas);
                        const sheetNameCalc = this.GetUniqueSheetName(workbook, names.calc);
                        this.GenerateSwellingReport(workbook, sheetNameMain, swellExp, wellName, subHeader);

                        this.GenerateSwellingGasCompDataReport(workbook, sheetNameGas, swellExp, wellName, subHeader);
                        this.GenerateSwellingCalculatedCompBalData(workbook, sheetNameCalc, swellExp, subHeader);
                    });
                }
                const tableItems = reportContent.tableItem;
                /*Appendix*/
                /*Appendix A: Fraction Reference Values*/
                if (fractionExp.components.length > 0) {
                    const content = tableItems.filter(x => x.experimentName === reportConstants.experimentNameFraction);
                    const letter = this.ExtractAppendixCode(content[0]?.title);
                    await this.GenerateCenteredSection(workbook, reportConstants.appendix + ' ' + letter, subHeader, reportConstants.appendix + ' ' + letter + '-' + reportConstants.fractionReferenceValues, '', 24, 16);
                    index = reportContent.tableItem.findIndex(x => x.title.includes(content[0]?.title));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                    }
                    await this.GenerateFractionAppendix(workbook, reportConstants.app + letter + ' ' + reportConstants.MW_SG, fractionExp, subHeader);
                }

                /*Appendix B: CCE Data*/
                if (summaryOfCCE.length > 0 || summaryOfCCEDew.length > 0) {
                    const content = tableItems.filter(x => x.experimentName === reportConstants.experimentNameCCE);
                    const letter = this.ExtractAppendixCode(content[0]?.title);
                    await this.GenerateCenteredSection(workbook, reportConstants.appendix + ' ' + letter, subHeader, reportConstants.appendix + ' ' + letter + '-' + reportConstants.CCEData, '', 24, 16);
                    index = reportContent.tableItem.findIndex(x => x.title.includes(content[0]?.title));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                    }
                    summaryOfCCE.map(exp => {
                        const sheetName = reportConstants.app + letter + ' ' + reportConstants.CCEData;
                        this.GenerateSummaryOfCCE(workbook, sheetName, exp, wellName, reportConstants.sample + ' ' + recombinedSampleId, reportConstants.appendix, letter !== null ? letter : '', reportType, subHeader);
                    });
                    /*Summary of CCE */

                    listOfCCE.map(exp => {
                        if (exp.cceProperties.isSwelling === false) {
                            const base = reportConstants.experimentNameCCE + ' ' + exp.cceProperties.studyTemperature + reportConstants.f;
                            const sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateCCE(workbook, sheetName, exp, wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader);
                            const sheetNamePlot = this.GetUniqueSheetName(workbook, base + reportConstants.plots);
                            this.GenerateCCEPlots(workbook, sheetNamePlot, exp, wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader);
                        }
                    });

                    /*Summary Of CCE DewPoint */
                    summaryOfCCEDew.map(exp => {
                        const base = reportConstants.app + letter + ' ' + reportConstants.CCEData + (summaryOfCCE.length > 0 ? reportConstants.specificGravitySubscript : '');
                        const sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateSummaryOfCCE(workbook, sheetName, exp, wellName, reportConstants.sample + ' ' + recombinedSampleId, reportConstants.appendix, letter !== null ? letter : '', reportType, subHeader);
                    });
                    /****************************************************************/

                    listOfCCEDewPoint.map(exp => {
                        if (exp.cceDewPointProperties.isSwelling === false) {
                            const base = reportConstants.experimentNameCCEDewPoint + ' ' + exp.cceDewPointProperties.studyTemperature + reportConstants.f;
                            const sheetName = this.GetUniqueSheetName(workbook, base);
                            this.GenerateCCEDewPoint(workbook, sheetName, exp, wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader);
                            const sheetNamePlot = this.GetUniqueSheetName(workbook, base + reportConstants.plots);
                            this.GenerateCCEDewPlots(workbook, sheetNamePlot, exp, wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader);
                        }
                    });
                }
                /*Appendix C-Swelling Test (Sep Gas) Data*/
                if (swellingExp.length > 0) {
                    const content = tableItems.filter(x => x.experimentName === reportConstants.experimentNameSwelling);
                    const letter = this.ExtractAppendixCode(content[0].title);
                    await this.GenerateCenteredSection(workbook, reportConstants.appendix + ' ' + letter, subHeader, reportConstants.appendix + ' ' + letter + reportConstants.swellingTestSepGasData, '', 24, 16);
                    index = reportContent.tableItem.findIndex(x => x.title.includes(content[0]?.title));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                    }
                    const batches = swellingExp.map((_, i) => {
                        const base = reportConstants.swellReport;
                        return {
                            main: base,
                            gas: `${base} ${reportConstants.swellReportGas} ${i + 1}`,
                            calc: `${base}${reportConstants.swellReportCalc} ${i + 1}`
                        };
                    });

                    for (let i = 0; i < swellingExp.length; i++) {
                        const swellExp = swellingExp[i];
                        const names = batches[i];
                        const sheetNameMain = reportConstants.app + letter + ' ' + this.GetUniqueSheetName(workbook, names.main);
                        this.GenerateSwellingReport(workbook, sheetNameMain, swellExp, wellName, subHeader, reportConstants.appendix, letter != null ? letter : '');
                        

                        //this foreach will change based on mol% and temp

                        for (const exp of listOfCCE) {
                            if (exp.cceProperties.isSwelling === true && exp.cceProperties.swellingSummaryId === swellExp.labRepeatId) {

                                const cceSatP = exp.bpTable.find((x: BPTable) => x.pointName === reportConstants.satP)?.pressure;
                                const temp = exp.cceProperties.studyTemperature;
                                const swellingSatP = (swellExp.swellReport.find((x: Vw_SwellReportData) => x.temperature === temp && x.saturationPressure === cceSatP)?.moleFracInjGas ?? 0) * 100;

                                const molPercentage = parseFloat(swellingSatP.toFixed(2));
                                const base = reportConstants.experimentNameCCE + molPercentage + '% ' + exp.cceProperties.studyTemperature + reportConstants.fInjG;
                                const sheetName = this.GetUniqueSheetName(workbook, base);
                                await this.GenerateCCESepGas(workbook, sheetName, exp, wellName, molPercentage, subHeader);
                                const sheetNamePlot = this.GetUniqueSheetName(workbook, base + reportConstants.plots);
                                await this.GenerateCCEGasPlots(workbook, sheetNamePlot, exp, wellName, molPercentage, subHeader);
                            }

                        }


                        for (const exp of listOfCCEDewPoint) {
                            if (exp.cceDewPointProperties.isSwelling === true && exp.cceDewPointProperties.swellingSummaryId === swellExp.labRepeatId) {

                                const cceSatP = exp.dpTable.find((x: DPTable) => x.pointName === reportConstants.pSat)?.pressure;
                                const temp = exp.cceDewPointProperties.studyTemperature;
                                const swellingSatP = (swellExp.swellReport.find((x: Vw_SwellReportData) => x.temperature === temp && x.saturationPressure === cceSatP)?.moleFracInjGas ?? 0) * 100;

                                const molPercentage = parseFloat(swellingSatP.toFixed(2));
                                const base = reportConstants.experimentNameCCE + molPercentage + '% ' + exp.cceDewPointProperties.studyTemperature + reportConstants.fInjG;
                                const sheetName = this.GetUniqueSheetName(workbook, base);
                                await this.GenerateCCEDewPointSepGas(workbook, sheetName, exp, wellName, molPercentage, subHeader);
                                const sheetNamePlot = this.GetUniqueSheetName(workbook, base + reportConstants.plots);
                                await this.GenerateCCEDewPointGasPlots(workbook, sheetNamePlot, exp, wellName, reportConstants.sample + ' ' + recombinedSampleId, molPercentage, subHeader);
                            }

                        }


                    }


                }
                /*Appendix D: Separator Test Material Balance*/
                if (msstExp.length > 0) {
                    const content = tableItems.filter(x => x.experimentName === reportConstants.experimentNameMSST);
                    const letter = this.ExtractAppendixCode(content[0].title);
                    await this.GenerateCenteredSection(workbook, reportConstants.appendix + ' ' + letter, subHeader, reportConstants.appendix + ' ' + letter + ': ' + reportConstants.separatorTestMaterialBalance, '', 24, 16);
                    index = reportContent.tableItem.findIndex(x => x.title.includes(content[0].title));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                    }
                    (msstExp.map(element => {
                        let base = reportConstants.appendix + ' ' + letter + ' ' + reportConstants.separatorTest;
                        let sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateSeparatorTest(workbook, sheetName, element, wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader, reportConstants.appendix + ' ' + letter + ': ');
                        base = reportConstants.sepTestVaporStageComps;
                        sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateSeparatorTestVaporCompositions(workbook, sheetName, element, subHeader);
                        base = reportConstants.stresidualOilCompositon;
                        sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateSeparatorTestResidualOilComp(workbook, sheetName, element, subHeader);
                        base = reportConstants.sepMaterialBalanceSummary;
                        sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateMaterialBalanceSummary(workbook, sheetName, element, subHeader);
                    }));
                }

                if (dleExp.length > 0) {
                    /*Appendix D-Diff Lib Material Balance*/
                    const content = tableItems.filter(x => x.experimentName === reportConstants.experimentNameDLE);
                    const letter = this.ExtractAppendixCode(content[0].title);
                    await this.GenerateCenteredSection(workbook, reportConstants.appendix + ' ' + letter, '', reportConstants.appendix + ' ' + letter + '-' + reportConstants.diffLibMaterialBalance, '', 24, 16);
                    index = reportContent.tableItem.findIndex(x => x.title.includes(content[0].title));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                    }
                    dleExp.map(element => {
                        let base = reportConstants.app + ' ' + letter + ' ' + reportConstants.diffLib;
                        let sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateDiffLibTest(workbook, sheetName, element, subHeader, reportConstants.appendix + ' ' + letter + ': ');
                        base = reportConstants.dlVaporStageComps;
                        sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateDiffLibTestVaporCompositions(workbook, sheetName, element, subHeader);
                        base = reportConstants.dlResidualOilComp;
                        sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateDiffLibTestResidualOilComp(workbook, sheetName, element, subHeader);
                        base = reportConstants.materialBalanceSummary;
                        sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateDiffLibMaterialBalanceSummary(workbook, sheetName, element, subHeader);
                    });
                }

                if (cvdExp.length > 0) {
                    const content = tableItems.filter(x => x.experimentName === reportConstants.experimentNameCVD);
                    const letter = this.ExtractAppendixCode(content[0].title);
                    await this.GenerateCenteredSection(workbook, reportConstants.appendix + ' ' + letter, subHeader, reportConstants.appendix + ' ' + letter + '- ' + reportConstants.constantVolumeDepletion, '', 24, 16);
                    index = reportContent.tableItem.findIndex(x => x.title.includes(content[0].title));
                    if (index > -1) {
                        reportContent.tableItem[index].page = (workbook.worksheets.length).toString();
                    }
                    (cvdExp.map(element => {
                        let base = reportConstants.app + ' ' + letter + ' ' + reportConstants.cvdTest;
                        let sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateCVD(workbook, sheetName, element, reportType === reportConstants.experimentNameCVD ? wellName + ' ' + reportConstants.dstSample : wellName, reportConstants.sample + ' ' + recombinedSampleId, subHeader, reportConstants.appendix + ' ' + letter + ': ');
                        base = reportConstants.cvdVaporStageComps;
                        sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateCVDVapor(workbook, sheetName, element, subHeader);
                        base = reportConstants.cvdLastStageOilComp;
                        sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateCVDStageOil(workbook, sheetName, element, subHeader);
                        base = reportConstants.cvdMaterialBalanceSummary;
                        sheetName = this.GetUniqueSheetName(workbook, base);
                        this.GenerateCVDMaterialBalance(workbook, sheetName, element, subHeader);
                    }));
                }
            }
            await this.GenerateTableOfContents(workbook, tableOfContentWorksheet, reportContent.tableItem, reportContent.wellDetails, subHeader);

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: reportConstants.excelMimeType });

            const fileName = this._reportGeneralUtilityService.BuildReportFileName(labTestData, reportType, reportConstants.xlsx);

            const formData = new FormData();
            formData.append(reportConstants.file, blob, fileName);

            fs.saveAs(blob, labTestData.dataLabTest[0].wellName + '_' + labTestData.dataLabTest[0].cylinder + reportConstants.sampleReport + reportType + reportConstants.xlsx);
            this.projectService.ReportUpload(formData).pipe(takeUntil(this.destroy$)).subscribe();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async GenerateDiffLibTest(workbook: Workbook, worksheetName: string, dleExp: DLEReportData, subHeader: string, appendix = '') {
        return this.reportPDFDiffLibRenderService.GenerateDiffLibTest(workbook, worksheetName, dleExp, subHeader, appendix);
    }

    async GenerateDiffLibTestVaporCompositions(workbook: Workbook, worksheetName: string, dleExp: DLEReportData, subHeader: string) {
        return this.reportPDFDiffLibRenderService.GenerateDiffLibTestVaporCompositions(workbook, worksheetName, dleExp, subHeader);
    }

    async GenerateDiffLibTestResidualOilComp(workbook: Workbook, worksheetName: string, dleExp: DLEReportData, subHeader: string) {
        return this.reportPDFDiffLibRenderService.GenerateDiffLibTestResidualOilComp(workbook, worksheetName, dleExp, subHeader);
    }

    async GenerateDiffLibMaterialBalanceSummary(workbook: Workbook, worksheetName: string, dleExp: DLEReportData, subHeader: string) {
        return this.reportPDFDiffLibRenderService.GenerateDiffLibMaterialBalanceSummary(workbook, worksheetName, dleExp, subHeader);
    }
    async GenerateFrontPage(workbook: Workbook, wellDetails: string[], frontPageNames: string[]) {
        try {
            const frontPageWorksheet = workbook.addWorksheet(reportConstants.frontPage);
            frontPageWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];

            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(frontPageWorksheet, undefined, {
                paperSize: undefined, // A4
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: true,
                fitToWidth: 1,//page 1
                fitToHeight: 1,// automatic
                scale: 95, // scale percentage
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });
            const frontpageexxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const frontpageexxonLogoId = workbook.addImage({
                base64: frontpageexxonLogo,
                extension: reportConstants.png,
            });

            frontPageWorksheet.addImage(frontpageexxonLogoId, {
                tl: { col: 2, row: 4 },
                ext: { width: 359, height: 69 }
            });

            const energyBase64 = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.energyLogo);
            const energyId = workbook.addImage({
                base64: energyBase64,
                extension: reportConstants.png,
            });

            frontPageWorksheet.addImage(energyId, {
                tl: { col: 3, row: 8 },
                ext: { width: 222.72, height: 40.32 }
            });

            //Static text change later
            /* Merged cells D12 to J13 for title placement */
            frontPageWorksheet.mergeCells(reportConstants.a12, reportConstants.j13);
            /* Bind the title value into D12 cell */
            const titleCell = frontPageWorksheet.getCell(reportConstants.a12);
            titleCell.value = reportConstants.reservoirFluidPVTStudy;
            titleCell.font = { name: reportConstants.eMprintCondensed, size: 20, bold: true };
            titleCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
            /* Merged cells F14 to H15 for wellbore placement */
            frontPageWorksheet.mergeCells(reportConstants.c14, reportConstants.h15);
            /* Bind the wellbore label into F14 cell */
            const WellboreCell = frontPageWorksheet.getCell(reportConstants.c14);
            WellboreCell.value = reportConstants.wellbore;
            WellboreCell.font = { name: reportConstants.eMprintCondensed, size: 20, bold: true };
            WellboreCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.top };

            const namesWell = wellDetails;// [reportConstants.wellbore4, reportConstants.mdtSample, reportConstants.date];
            if (namesWell && namesWell.length > 0) {
                let cellNo = 16;
                namesWell.forEach(name => {
                    if (name !== '') {
                        /* Merged cells F16 to H16 for wellbore details placement */
                        frontPageWorksheet.mergeCells(reportConstants.cChar + cellNo, reportConstants.h + cellNo);
                        /* Bind wellbore details value into F16 cell */
                        const cell = reportConstants.cChar + cellNo;
                        const namesWellCell = frontPageWorksheet.getCell(cell);
                        namesWellCell.value = name;
                        namesWellCell.font = { name: reportConstants.eMprintCondensed, size: 12, bold: false };
                        namesWellCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.top };
                        cellNo++;
                    }
                });
            }
            else {
                /* Bind wellbore details value into F16, F17, and F18 cells */
                frontPageWorksheet.getCell(reportConstants.c16).value = reportConstants.wellbore4;
                frontPageWorksheet.getCell(reportConstants.c17).value = reportConstants.mdtSample;
                frontPageWorksheet.getCell(reportConstants.c18).value = reportConstants.date;
            }

            /* Merged cells D20 to J21 for EMHC placement */
            frontPageWorksheet.mergeCells(reportConstants.a20, reportConstants.j21);
            /* Bind EMHC label into D20 cell */
            const EMHCCell = frontPageWorksheet.getCell(reportConstants.a20);
            EMHCCell.value = reportConstants.emhc;
            EMHCCell.font = { name: reportConstants.eMprintCondensed, size: 18, bold: true };
            EMHCCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.top };

            const namesLength = frontPageNames?.length || 0;
            const nextRowNo = (23 + namesLength);
            /* Merged cells F22 to H(nextRowNo) for front page names placement */
            frontPageWorksheet.mergeCells(reportConstants.c22, reportConstants.h + (nextRowNo));
            /* Bind front page names value into F22 cell */
            const textVBoxCell = frontPageWorksheet.getCell(reportConstants.c22);
            textVBoxCell.value = frontPageNames?.join('\n');
            textVBoxCell.font = { name: reportConstants.eMprintCondensed, bold: false };
            textVBoxCell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.top, wrapText: true };

            //end

            const officeBase64 = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.frontpageOffice);
            const officeId = workbook.addImage({
                base64: officeBase64,
                extension: reportConstants.png,
            });

            frontPageWorksheet.addImage(officeId, {
                tl: { col: 0, row: (nextRowNo + 1) },
                ext: { width: 654, height: 307.2 }
            });

            this.AddOuterBorder(frontPageWorksheet, 3, (nextRowNo + 21), 1, 10);

        } catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }
    }

    async GenerateCoverLetter(workbook: Workbook, coverDate: string, addressToName: string, fromName: string, coverContent: string[], subHeader: string, dearName: string, finalReportDetails: FinalReportDetailsModel[]) {
        try {
            const coverLetterWorksheet = workbook.addWorksheet(reportConstants.coverLetter);
            coverLetterWorksheet.getColumn(reportConstants.c).width = 4;
            const finalDetails = finalReportDetails.find(x => x);

            coverLetterWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];

            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(coverLetterWorksheet, undefined, {
                paperSize: undefined, // A4
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: false,
                fitToWidth: 0,//page 1
                fitToHeight: 0,// 1 page , 0 automatic
                scale: 98, // scale percentage,
                margins: {
                    left: 0.25,
                    right: 0.25,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                }
            });

            /* Set the width of column B for UI design. */
            coverLetterWorksheet.getColumn(reportConstants.a).width = 2;

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(coverLetterWorksheet, subHeader, reportConstants.c1, reportConstants.j1);

            // Sender Address Block (Left-aligned)
            const senderLines = [finalDetails?.fromAddress1 ?? "", finalDetails?.fromAddress2 ?? "", finalDetails?.fromAddress3 ?? "", finalDetails?.fromAddress4 ?? ""] as string[];
            let rowNo = 3;
            senderLines.forEach((line, index) => {
                /* Merged cells C(rowNo) to G(rowNo) for sender address placement */
                coverLetterWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.gColumn + rowNo);
                const textSenderAddress = coverLetterWorksheet.getCell(reportConstants.c + rowNo);
                textSenderAddress.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
                if (index < 2) {
                    textSenderAddress.value = line;
                    textSenderAddress.font = { name: reportConstants.eMprintCondensed, size: 10, bold: true };
                } else {
                    textSenderAddress.value = line;
                    textSenderAddress.font = { name: reportConstants.eMprintCondensed, size: 9, bold: false };
                }
                rowNo++;
            });

            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });
            /* Merged cells I(rowNo) to K(rowNo + 1) for Exxon logo placement */
            coverLetterWorksheet.addImage(exxonLogoId, {
                tl: { col: 7, row: rowNo },
                ext: { width: 151, height: 31 }
            });

            rowNo += 4;
            /*Merged cells C(rowNo) to D(rowNo) for cover date placement */
            coverLetterWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.d + rowNo);
            const textCell4 = coverLetterWorksheet.getCell(reportConstants.c + rowNo);
            textCell4.value = coverDate;
            textCell4.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
            textCell4.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            rowNo += 2;
            /*Merged cells C(rowNo) to D(rowNo) for salutation placement */
            coverLetterWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.d + rowNo);
            /*Bind salutation value into C(rowNo) cell */
            const textCell5 = coverLetterWorksheet.getCell(reportConstants.b + rowNo);
            textCell5.value = addressToName;
            textCell5.font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
            textCell5.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            rowNo++;

            const senderLinesTo = [finalDetails?.toAddress1 ?? "", finalDetails?.toAddress2 ?? "", finalDetails?.toAddress3 ?? "", finalDetails?.toAddress4 ?? ""] as string[];
            senderLinesTo.forEach((line) => {
                /* Merged cells C(rowNo) to G(rowNo) for sender address placement */
                coverLetterWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.gColumn + rowNo);
                const textSenderAddress = coverLetterWorksheet.getCell(reportConstants.b + rowNo);
                textSenderAddress.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
                textSenderAddress.value = line;
                textSenderAddress.font = { name: reportConstants.eMprintCondensed, size: 10, bold: false };
                rowNo++;
            });

            rowNo += 2;
            /*Merged cells C(rowNo) to D(rowNo) for dear name placement */
            coverLetterWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.d + rowNo);
            /*Bind dear name value into C(rowNo) cell */
            const textCell7 = coverLetterWorksheet.getCell(reportConstants.b + rowNo);
            textCell7.value = reportConstants.dear + dearName + ',';
            textCell7.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
            textCell7.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            rowNo += 1;
            const bodyLines = coverContent;
            bodyLines.forEach((paragraph, index) => {
                const paragraphLength = paragraph.length;
                const rowCount = Math.floor(paragraphLength / 70); // Assuming 70 characters per line
                let mergeRows = rowNo + rowCount;
                /*Merged cells C(rowNo) to K(mergeRows) for paragraph placement */
                if (index === 2) {
                    mergeRows = mergeRows + 1;
                }
                coverLetterWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.j + mergeRows);
                /*Bind paragraph value into C(rowNo) cell */
                const textCellBody = coverLetterWorksheet.getCell(reportConstants.b + rowNo);
                textCellBody.value = paragraph;
                textCellBody.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
                if (index === 2) {
                    textCellBody.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle, wrapText: true };
                } else {
                    textCellBody.alignment = { horizontal: 'justify', vertical: reportConstants.middle, wrapText: true };
                }

                rowNo = mergeRows + 1; // Move to the next row after the paragraph
            });
            /*Merged cells G(rowNo) to H(rowNo) for regards placement */
            coverLetterWorksheet.mergeCells(reportConstants.i + rowNo, reportConstants.j + rowNo);
            /*Bind regards value into G(rowNo) cell */
            const textCell11 = coverLetterWorksheet.getCell(reportConstants.i + rowNo);
            textCell11.value = reportConstants.regards;
            textCell11.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
            textCell11.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            rowNo++;
            /*Merged cells G(rowNo) to H(rowNo) for from name placement */
            coverLetterWorksheet.mergeCells(reportConstants.i + rowNo, reportConstants.j + rowNo);
            /*Bind from name value into G(rowNo) cell */
            const textCell12 = coverLetterWorksheet.getCell(reportConstants.i + rowNo);
            textCell12.value = fromName;
            textCell12.font = { size: 11, bold: false };
            textCell12.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            rowNo += 2;
            const splitData = (finalDetails?.by ?? "").split(",");
            /*Merged cells G(rowNo) to I(rowNo) for signature placement */
            coverLetterWorksheet.mergeCells(reportConstants.gColumn + rowNo, reportConstants.i + rowNo);
            /*Bind signature value into G(rowNo) cell */
            const textCell13 = coverLetterWorksheet.getCell(reportConstants.b + rowNo);
            textCell13.value = "By ________________";
            textCell13.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
            textCell13.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            rowNo += 2;
            for (const item of splitData) {
                /*Merged cells C(rowNo) to D(rowNo) for NA placement */
                coverLetterWorksheet.mergeCells(reportConstants.b + rowNo, reportConstants.d + rowNo);
                /*Bind NA value into C(rowNo) cell */
                const textCell14 = coverLetterWorksheet.getCell(reportConstants.b + rowNo);
                textCell14.value = item ?? "";
                textCell14.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
                textCell14.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
                rowNo++;
            }

            /*Merged cells C(rowNo) to D(rowNo) for attachment placement */
           /*Bind attachment value into C(rowNo) cell */

            const labelCell = coverLetterWorksheet.getCell(reportConstants.b + rowNo);
            labelCell.value = reportConstants.attachment;
            labelCell.font = {
                name: reportConstants.eMprintCondensed,
                size: 11,
                bold: false
            };
            labelCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.top };

            coverLetterWorksheet.mergeCells(reportConstants.d + rowNo, reportConstants.j + rowNo);
            const attachmentCell = coverLetterWorksheet.getCell(reportConstants.d + rowNo);
            attachmentCell.value = finalDetails?.attachment ?? '';
            attachmentCell.font = {
                name: reportConstants.eMprintCondensed,
                size: 11,
                bold: false
            };
            attachmentCell.alignment = {
                horizontal: reportConstants.left,
                vertical: reportConstants.top,
                wrapText: true
            };
            coverLetterWorksheet.getRow(rowNo).height = 45;
            rowNo++;

            this.AddOuterBorder(coverLetterWorksheet, 3, rowNo, 2, 10);

        } catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }
    }

    async GenerateTableOfContents(workbook: Workbook, tableOfContentsWorksheet: Worksheet, tableItem: ReportContentTable[], wellDetails: string[], subHeadder: string) {
        return this.reportPDFWorkbookRenderService.GenerateTableOfContents(workbook, tableOfContentsWorksheet, tableItem, wellDetails, subHeadder);
    }

    async GenerateAnalysesMethods(workbook: Workbook, summaryContent: SampleAnalyses[], subHeader: string) {
        return this.reportPDFWorkbookRenderService.GenerateAnalysesMethods(workbook, summaryContent, subHeader);
    }

    async GenerateSampleInfo(
        workbook: Workbook,
        worksheetName: string,
        wellInformation: WellInformation,
        sampleInformation: SampleInformation,
        sampleInformationSeparator: SampleInformationSeparator,
        sampleInformationDetails: SampleInformationDetails[],
        reservoirProperties: ReservoirProperties[],
        reservoirPropertiesDST: ReservoirPropertiesDST[],
        saturatedProperties: SaturatedProperties[],
        sampleInformationDetailsSeparator: SampleInformationDetailsSeparator[],
        reservoirPropertiesSeparator: ReservoirPropertiesSeparator[],
        reportType: string,
        subHeader: string
    ) {
        try {
            const sampleInfoWorksheet = workbook.addWorksheet(worksheetName);

            sampleInfoWorksheet.views = [{ state: reportConstants.normal, showGridLines: false, style: reportConstants.pageLayout }];


            this._reportGeneralUtilityService.ApplyFooterAndPageSetup(sampleInfoWorksheet, undefined, {
                paperSize: undefined, // A4
                orientation: reportConstants.portrait, //we can set it to 'portrait' or 'landscape'
                fitToPage: false,
                fitToWidth: 0,//page 1
                fitToHeight: 0,// automatic
                scale: 80,
                margins: {
                    left: 0.75,
                    right: 0.50,
                    top: 0.25,
                    bottom: 0.25,
                    header: 0,
                    footer: 0
                },
            });
            const exxonLogo = await this._reportGeneralUtilityService.convertImageToBase64(reportConstants.pageLogoName);
            const exxonLogoId = workbook.addImage({
                base64: exxonLogo,
                extension: reportConstants.png,
            });

            sampleInfoWorksheet.getRow(1).height = 20;
            sampleInfoWorksheet.addImage(exxonLogoId, {
                tl: { col: 0, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(sampleInfoWorksheet, subHeader, reportConstants.c1, reportConstants.j1);

            const wellInfoLabels: Record<keyof WellInformation, string> = reportConstants.wellboreLabel;
            const sampleInfoLabels: Record<keyof SampleInformationLabel, string> = reportConstants.sampleInfoLabels;
            const sampleInfoSeparatorLabels: Record<keyof SampleInformationSeparator, string> = reportConstants.sampleInfoSeparatorLabels;
            /* Merged cells B2 to F3 for well sampling information */
            sampleInfoWorksheet.mergeCells(reportConstants.b3, reportConstants.h4);
            const textWellSamplingInfoCell = sampleInfoWorksheet.getCell(reportConstants.b3);
            textWellSamplingInfoCell.value = wellInformation.well + ' ' + reportConstants.samplingInformation;
            textWellSamplingInfoCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textWellSamplingInfoCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            /* Merged cells B5 to E5 for reservoir and well information */
            sampleInfoWorksheet.mergeCells(reportConstants.b6, reportConstants.e6);
            const ReservoirWellInfoCell = sampleInfoWorksheet.getCell(reportConstants.b6);
            ReservoirWellInfoCell.value = reportConstants.reservoirAndWellInformation;
            ReservoirWellInfoCell.font = { name: reportConstants.eMprintCondensed, size: 10, bold: true };
            ReservoirWellInfoCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            // Add light grey background to C5-F5
            for (let col = 2; col <= 10; col++) {
                const cell = sampleInfoWorksheet.getCell(6, col);
                cell.fill = {
                    type: reportConstants.pattern,
                    pattern: reportConstants.solid,
                    fgColor: { argb: reportConstants.ffdcdcdc } // light grey background
                };
            }
            let rowNo = 8;
            sampleInfoWorksheet.getColumn(reportConstants.j).width = 12;
            (Object.keys(wellInformation) as (keyof WellInformation)[]).forEach(key => {

                const label = wellInfoLabels[key] || key;
                const value = wellInformation[key] || '';

                const dots = '.'.repeat(75 - label.length);
                const line = `${label} ${dots}`;
                /* Merged cells C(rowNo) to G(rowNo) for well information label */
                sampleInfoWorksheet.mergeCells(reportConstants.c + rowNo, reportConstants.gColumn + rowNo);
                /* Bind well information label into C(rowNo) cell */
                const wellInfoLabelCell = sampleInfoWorksheet.getCell(reportConstants.c + rowNo);
                wellInfoLabelCell.value = line;
                wellInfoLabelCell.font = { name: reportConstants.eMprintCondensed, size: 10, bold: false };
                wellInfoLabelCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
                /* Merged cells H(rowNo) to I(rowNo) for well information value */
                sampleInfoWorksheet.mergeCells(reportConstants.h + rowNo, reportConstants.j + rowNo);
                /* Bind well information value into H(rowNo) cell */
                const wellInfoValueCell = sampleInfoWorksheet.getCell(reportConstants.h + rowNo);
                setNumericOrTextCellValue(wellInfoValueCell, value);
                wellInfoValueCell.font = { name: reportConstants.eMprintCondensed, size: 10, bold: false };
                wellInfoValueCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

                rowNo++;
            });

            rowNo++;
            /* Merged cells C(rowNo) to E(rowNo) for sampling information label */
            sampleInfoWorksheet.mergeCells(reportConstants.c + rowNo, reportConstants.e + rowNo);
            /* Bind sampling information value into C(rowNo) cell */
            const samplingInfoCell = sampleInfoWorksheet.getCell(reportConstants.c + rowNo);
            samplingInfoCell.value = reportConstants.samplingInformation;
            samplingInfoCell.font = { name: reportConstants.eMprintCondensed, size: 10, bold: true };
            samplingInfoCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            // Add light grey background to C5-F5
            for (let col = 3; col <= 10; col++) {
                const cell = sampleInfoWorksheet.getCell(rowNo, col);
                cell.fill = {
                    type: reportConstants.pattern,
                    pattern: reportConstants.solid,
                    fgColor: { argb: reportConstants.ffdcdcdc } // light grey background
                };
            }

            rowNo += 2;

            if (reportType.toLowerCase() === reportConstants.reportTypeSeparatorTest) {
                (Object.keys(sampleInformationSeparator) as (keyof SampleInformationSeparator)[]).forEach(key => {

                    const label = sampleInfoSeparatorLabels[key] || key;
                    const value = sampleInformationSeparator[key] || '';

                    const dots = '.'.repeat(70 - label.length);
                    const line = `${label} ${dots}`;
                    /* Merged cells C(rowNo) to G(rowNo) for sampling information labels */
                    sampleInfoWorksheet.mergeCells(reportConstants.c + rowNo, reportConstants.gColumn + rowNo);
                    /* Bind sampling information label values into C(rowNo) cell */
                    const samplingInfoLabelCell = sampleInfoWorksheet.getCell(reportConstants.c + rowNo);
                    samplingInfoLabelCell.value = line;
                    samplingInfoLabelCell.font = { name: reportConstants.eMprintCondensed, size: 10, bold: false };
                    samplingInfoLabelCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
                    /* Merged cells H(rowNo) to I(rowNo) for sampling information values */
                    sampleInfoWorksheet.mergeCells(reportConstants.h + rowNo, reportConstants.j + rowNo);
                    /* Bind sampling information values into H(rowNo) cell */
                    const samplingInfoValueCell = sampleInfoWorksheet.getCell(reportConstants.h + rowNo);
                    setNumericOrTextCellValue(samplingInfoValueCell, value);
                    samplingInfoValueCell.font = { name: reportConstants.eMprintCondensed, size: 10, bold: false };
                    samplingInfoValueCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

                    rowNo++;

                });
            } else {
                (Object.keys(sampleInformation) as (keyof SampleInformationLabel)[]).forEach(key => {

                    const label = sampleInfoLabels[key] || key;
                    const value = sampleInformation[key] || '';

                    const dots = '.'.repeat(70 - label.length);
                    const line = `${label} ${dots}`;
                    /* Merged cells C(rowNo) to G(rowNo) for sampling information labels */
                    sampleInfoWorksheet.mergeCells(reportConstants.c + rowNo, reportConstants.gColumn + rowNo);
                    /* Bind sampling information label values into C(rowNo) cell */
                    const SamplingInfoLabelCell = sampleInfoWorksheet.getCell(reportConstants.c + rowNo);
                    SamplingInfoLabelCell.value = line;
                    SamplingInfoLabelCell.font = { name: reportConstants.eMprintCondensed, size: 10, bold: false };
                    SamplingInfoLabelCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
                    /* Merged cells H(rowNo) to I(rowNo) for sampling information values */
                    sampleInfoWorksheet.mergeCells(reportConstants.h + rowNo, reportConstants.j + rowNo);
                    /* Bind sampling information values into H(rowNo) cell */
                    const samplingInfoValueCell = sampleInfoWorksheet.getCell(reportConstants.h + rowNo);
                    setNumericOrTextCellValue(samplingInfoValueCell, value);
                    samplingInfoValueCell.font = { name: reportConstants.eMprintCondensed, size: 10, bold: false };
                    samplingInfoValueCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

                    rowNo++;

                });

                rowNo += 2;
                if (reportType.toLowerCase() === reportConstants.reportTypeDST) {
                    /* Merged cells C(rowNo) to G(rowNo) for sampling information note */
                    sampleInfoWorksheet.mergeCells(reportConstants.c + rowNo, reportConstants.gColumn + rowNo);
                    /* Bind sampling information note into C(rowNo) cell */
                    const noteCell = sampleInfoWorksheet.getCell(reportConstants.c + rowNo);
                    noteCell.value = reportConstants.depthNote;
                    noteCell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
                    noteCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
                }
            }

            //Page 1 borders
            this.AddOuterBorder(sampleInfoWorksheet, 3, rowNo + 5, 2, 10);
            // End of Page 1

            // Page 2 starts here
            sampleInfoWorksheet.addImage(exxonLogoId, {
                tl: { col: 12, row: 0 },
                ext: { width: 151, height: 31 }
            });

            this._reportGeneralUtilityService.AddSubHeaderToWorksheet(sampleInfoWorksheet, subHeader, reportConstants.o1, reportType.toLowerCase()==reportConstants.reportTypeSeparatorTest ? reportConstants.u1 : reportConstants.v1);

            //Well And Sampling Details
            /* Merged cells K2 to O3 for well and sampling details */
            sampleInfoWorksheet.mergeCells(reportConstants.m3, reportConstants.s4);
            /* Bind well and sampling details into K2 cell */
            const textWellSamplingInfoCell2 = sampleInfoWorksheet.getCell(reportConstants.m3);
            textWellSamplingInfoCell2.value = wellInformation.well + (reportType.toLowerCase() === reportConstants.reportTypeDST ? ' ' + reportConstants.sampleOverview : ' ' + reportConstants.samplingInformation);
            textWellSamplingInfoCell2.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textWellSamplingInfoCell2.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            rowNo = 5;
            if (reportType.toLowerCase() === reportConstants.reportTypeSeparatorTest) {
                const sampleHead = reportConstants.sampleHeader;
                const sampleBody = sampleInformationDetailsSeparator.map(val => [
                    val.sampleId ?? reportConstants.dash,
                    val.sampleDepth ?? reportConstants.dash,
                    val.sampleType ?? reportConstants.dash,
                    val.reservoir ?? reportConstants.dash,
                    val.bottleType ?? reportConstants.dash,
                    val.sampleVolume ?? reportConstants.dash,
                    val.openingTemperature ?? reportConstants.dash,
                    val.openingPressure ?? reportConstants.dash
                ]);

                const tableStartRow = rowNo++;

                // Add Table
                GenerateSampleInfoTable(sampleInfoWorksheet, sampleHead, sampleBody, tableStartRow, 13);
                sampleInfoWorksheet.getColumn(reportConstants.w).width = 8;
                rowNo = tableStartRow + sampleHead.length + sampleBody.length;
                rowNo++;
            }
            else {
                const sampleHead = reportConstants.sampleHeader1;
                const sampleBody = sampleInformationDetails.map(val => [
                    val.sampleId ?? reportConstants.dash,
                    val.sampleDepth ?? reportConstants.dash,
                    val.sampleType ?? reportConstants.dash,
                    val.reservoir ?? reportConstants.dash,
                    val.bottleType ?? reportConstants.dash,
                    val.sampleVolume ?? reportConstants.dash,
                    val.bottleSN ?? reportConstants.dash,
                    val.closingTemperature ?? reportConstants.dash,
                    val.closingPressure ?? reportConstants.dash,
                    val.openingTemperature ?? reportConstants.dash,
                    val.openingPressure ?? reportConstants.dash
                ]);

                const tableStartRow = rowNo++;

                // Add Table
                GenerateSampleInfoTable(sampleInfoWorksheet, sampleHead, sampleBody, tableStartRow, 13);

                rowNo = tableStartRow + sampleHead.length + sampleBody.length;
                rowNo++;
            }

            //Reservoir Properties
            /* Merged cells K(rowNo) to O(rowNo+1) for reservoir properties */
            sampleInfoWorksheet.mergeCells(reportConstants.m + rowNo, reportConstants.s + (rowNo + 1));
            /* Bind reservoir properties label into K(rowNo) cell */
            const textReservoirPropertiesCell = sampleInfoWorksheet.getCell(reportConstants.m + rowNo);
            textReservoirPropertiesCell.value = wellInformation.well + ' ' + reportConstants.reservoirProperties;
            textReservoirPropertiesCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textReservoirPropertiesCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            rowNo += 2;
            if (reportType.toLowerCase() === reportConstants.reportTypeMDT) {
                const reservoirHead = reportConstants.reservoirHeadUpdated;
                const reservoirBody: string[][] = reservoirProperties.map(val => [
                    String(val.sampleId ?? reportConstants.dash),
                    val.sampleDepth ?? reportConstants.dash,
                    String(this._reportGeneralUtilityService.FormatToFourDecimals(val.reservoirTemperature) ?? reportConstants.dash),
                    String(this._reportGeneralUtilityService.FormatToFourDecimals(val.reservoirPressure) ?? reportConstants.dash),
                    String(this._reportGeneralUtilityService.FormatToFourDecimals(val.viscosity) ?? reportConstants.dash),
                    String(this._reportGeneralUtilityService.FormatToFourDecimals(val.density) ?? reportConstants.dash),
                    String(this._reportGeneralUtilityService.FormatToFourDecimals(val.formationVolumeFactor) ?? reportConstants.dash),
                    String(this._reportGeneralUtilityService.FormatToFourDecimals(val.contaminatedGOR) ?? reportConstants.dash),
                    String(this._reportGeneralUtilityService.FormatToFourDecimals(val.uncontaminatedGOR) ?? reportConstants.dash),
                    String(this._reportGeneralUtilityService.FormatToFourDecimals(val.contaminantInLiveFluid) ?? reportConstants.dash),
                    String(this._reportGeneralUtilityService.FormatToFourDecimals(val.contaminantInResidualFluid) ?? reportConstants.dash)
                ]);

                const tableStartRow = rowNo++;
                GenerateSampleInfoTable(sampleInfoWorksheet, reservoirHead, reservoirBody, tableStartRow, 13,reportConstants.merge);
                rowNo = tableStartRow + reservoirHead.length + reservoirBody.length;
                rowNo++;
            }
            else if (reportType.toLowerCase() === reportConstants.reportTypeDST) {
                const reservoirHead = reportConstants.reservoirHeadSeparatorUpdated;
                const reservoirBody: string[][] = reservoirPropertiesDST.map(val => [
                    val.sampleId ?? reportConstants.dash,
                    val.sampleDepth ?? reportConstants.dash,
                    this._reportGeneralUtilityService.FormatToFourDecimals(val.reservoirTemperature) ?? reportConstants.dash,
                    this._reportGeneralUtilityService.FormatToFourDecimals(val.reservoirPressure) ?? reportConstants.dash,
                    this._reportGeneralUtilityService.FormatToFourDecimals(val.viscosity) ?? reportConstants.dash,
                    this._reportGeneralUtilityService.FormatToFourDecimals(val.density) ?? reportConstants.dash,
                    this._reportGeneralUtilityService.FormatToFourDecimals(val.formationVolumeFactor) ?? reportConstants.dash,
                    this._reportGeneralUtilityService.FormatToFourDecimals(val.gor) ?? reportConstants.dash
                ]);

                const tableStartRow = rowNo++;
                GenerateSampleInfoTable(sampleInfoWorksheet, reservoirHead, reservoirBody, tableStartRow, 13,reportConstants.merge);

                rowNo = tableStartRow + reservoirHead.length + reservoirBody.length;
                rowNo++;
            }
            else if (reportType.toLowerCase() === reportConstants.reportTypeSeparatorTest) {
                const reservoirHead = reportConstants.reservoirHeadMinimalUpdated;
                const reservoirBody = reservoirPropertiesSeparator.map(val => [
                    val.sampleId ?? reportConstants.dash,
                    val.sampleDepth ?? reportConstants.dash,
                    this._reportGeneralUtilityService.FormatToFourDecimals(val.reservoirTemperature) ?? reportConstants.dash,
                    this._reportGeneralUtilityService.FormatToFourDecimals(val.reservoirPressure) ?? reportConstants.dash,
                    this._reportGeneralUtilityService.FormatToFourDecimals(val.viscosity) ?? reportConstants.dash,
                    this._reportGeneralUtilityService.FormatToFourDecimals(val.density) ?? reportConstants.dash
                ]);

                const tableStartRow = rowNo++;
                GenerateSampleInfoTable(sampleInfoWorksheet, reservoirHead, reservoirBody, tableStartRow, 13,reportConstants.merge);

                rowNo = tableStartRow + reservoirHead.length + reservoirBody.length;
                rowNo++;
            }

            //Saturated Properties
            /* Merged cells K(rowNo) to O(rowNo+1) for saturated properties */
            sampleInfoWorksheet.mergeCells(reportConstants.m + rowNo, reportConstants.s + (rowNo + 1));
            /* Bind saturated properties label into K(rowNo) cell */
            const textSaturatedPropertiesCell = sampleInfoWorksheet.getCell(reportConstants.m + rowNo);
            textSaturatedPropertiesCell.value = wellInformation.well + ' ' + reportConstants.saturatedProperties;
            textSaturatedPropertiesCell.font = { name: reportConstants.eMprintCondensed, size: 14, bold: true };
            textSaturatedPropertiesCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };

            rowNo++;
            const saturatedHead = reportConstants.saturatedHeadUpdated;
            const saturatedBody: string[][] = saturatedProperties.map(val => [
                String(val.sampleId ?? reportConstants.dash),
                String(val.sampleDepth ?? reportConstants.dash),
                String(this._reportGeneralUtilityService.FormatToFourDecimals(val.saturationTemperature) ?? reportConstants.dash),
                String(this._reportGeneralUtilityService.FormatToFourDecimals(val.saturationPressure) ?? reportConstants.dash),
                String(this._reportGeneralUtilityService.FormatToFourDecimals(val.viscosityAtSaturationPressure) ?? reportConstants.dash),
                String(this._reportGeneralUtilityService.FormatToFourDecimals(val.densityAtSaturationPressure) ?? reportConstants.dash)
            ]);

            const tableStartRow = rowNo+2;
            GenerateSampleInfoTable(sampleInfoWorksheet, saturatedHead, saturatedBody, tableStartRow, 13, reportConstants.merge);

            rowNo = tableStartRow + saturatedHead.length + saturatedBody.length;
            rowNo++;

            if (reportType.toLowerCase() === reportConstants.reportTypeDST) {
                rowNo += 2;
                /* Merged cells K(rowNo) to O(rowNo) for sampling information comments */
                sampleInfoWorksheet.mergeCells(reportConstants.m + rowNo, reportConstants.q + rowNo);
                /* Bind sampling information comments into K(rowNo) cell */
                const noteCell = sampleInfoWorksheet.getCell(reportConstants.m + rowNo);
                noteCell.value = reportConstants.depthNote;
                noteCell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
                noteCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
            }
            //Page 2 borders
            if (reportType.toLowerCase() === reportConstants.reportTypeMDT)
            {
                
                sampleInfoWorksheet.getColumn(reportConstants.t).width = 10;
                sampleInfoWorksheet.getColumn(reportConstants.u).width = 10;
                sampleInfoWorksheet.getColumn(reportConstants.v).width = 10;
                sampleInfoWorksheet.getColumn(reportConstants.w).width = 10;
            }
            sampleInfoWorksheet.getColumn(reportConstants.m).width = 10;
            sampleInfoWorksheet.getColumn(reportConstants.o).width = 10;
            sampleInfoWorksheet.getColumn(reportConstants.p).width = 10;
            sampleInfoWorksheet.getColumn(reportConstants.q).width = 10;
            sampleInfoWorksheet.getColumn(reportConstants.r).width = 10;

            // Slightly increase row height for rows 8 through rowNo
            for (let r = 8; r <= 35; r++) {
                sampleInfoWorksheet.getRow(r).height = 17;
            }
            this.AddOuterBorder(sampleInfoWorksheet, 3, rowNo + 12, 13, reportType.toLowerCase() !== reportConstants.reportTypeSeparatorTest ? 23 : 20);
        }
        catch (error) {
            console.error(reportConstantsMessage.errorGeneratingSheet, error);
        }

        function GenerateSampleInfoTable(worksheet: Worksheet, tableHeader: string[][], tableBody: string[][], tableStartRow: number, tableStartColumn: number,pageAndTableType="") {
            // Write header rows
            tableHeader.forEach((headerRow, idx) => {
                let tempIdx = idx;
                headerRow.forEach((header, colIdx) => {
                    const row = tableStartRow + idx;
                    const col = tableStartColumn + colIdx;

                    if (pageAndTableType !== "") {
                        worksheet.mergeCells(row, // start row
                            col, // start column
                            row + 1, // next row
                            col // same column
                        );

                        tempIdx = tempIdx + 1;

                        const cell = worksheet.getCell(row, col);
                        cell.value = header;
                        cell.font = { name: reportConstants.eMprintCondensed, size: 9, bold: false };
                        cell.alignment = { horizontal: reportConstants.center, vertical: "justify", wrapText: true };
                        cell.fill = {
                            type: reportConstants.pattern,
                            pattern: reportConstants.solid,
                            fgColor: { argb: reportConstants.ffdcdcdc }
                        };
                    }
                    else {
                        const cell = worksheet.getCell(tableStartRow + idx, tableStartColumn + colIdx);
                        cell.value = header;
                        cell.font = { name: reportConstants.eMprintCondensed, size: 9, bold: false };
                        cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle, wrapText: true };
                        cell.fill = {
                            type: reportConstants.pattern,
                            pattern: reportConstants.solid,
                            fgColor: { argb: reportConstants.ffdcdcdc }
                        };
                    }
                    worksheet.getColumn(colIdx + 11).width = 8; // Increase column width
                });
                if(tempIdx != idx) {
                    idx = tempIdx;
                    pageAndTableType="";
                }
            });

            tableBody.forEach((row, rowIdx) => {
                row.forEach((cellValue, colIdx) => {
                    const cell = worksheet.getCell(tableStartRow + tableHeader.length + rowIdx, tableStartColumn + colIdx);
                    setNumericOrTextCellValue(cell, cellValue);
                    cell.font = { name: reportConstants.eMprintCondensed, size: 9, bold: false };
                    cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                });
            });
        }
    }

    async GenerateCenteredSection(workbook: Workbook, worksheetName: string, subHeader: string, content: string, subContent = "", fontSizeTitle = 24, fontSizeSubTitle = 16) {
        return this.reportPDFWorkbookRenderService.GenerateCenteredSection(workbook, worksheetName, subHeader, content, subContent, fontSizeTitle, fontSizeSubTitle);
    }

    async GenerateCompositionalAnalysis(workbook: Workbook, worksheetName: string, bcaExp: BCAReports, wellName: string, sampleName: string, subHeader: string, recombined = "") {
        return this.reportPDFCompositionRenderService.GenerateCompositionalAnalysis(workbook, worksheetName, bcaExp, wellName, sampleName, subHeader, recombined);
    }

    async GenerateSingleStageFlashGORData(workbook: Workbook, worksheetName: string, bcaExp: BCAReports, wellName: string, sampleName: string, subHeader: string, recombined = "") {
        return this.reportPDFCompositionRenderService.GenerateSingleStageFlashGORData(workbook, worksheetName, bcaExp, wellName, sampleName, subHeader, recombined);
    }

    async GenerateMaterialBalanceCheckAndHoffmanPlot(workbook: Workbook, worksheetName: string, bcaExp: BCAReports, wellName: string, sampleName: string, subHeader: string, recombined = "") {
        return this.reportPDFCompositionRenderService.GenerateMaterialBalanceCheckAndHoffmanPlot(workbook, worksheetName, bcaExp, wellName, sampleName, subHeader, recombined);
    }

    async GenerateCVD(workbook: Workbook, worksheetName: string, cvdExp: CVDReportData, wellName: string, sampleName: string, subHeader: string, appendix = '') {
        return this.reportPDFCvdRenderService.GenerateCVD(workbook, worksheetName, cvdExp, wellName, sampleName, subHeader, appendix);
    }

    async GenerateCVDVapor(workbook: Workbook, worksheetName: string, cvdExp: CVDReportData, subHeader: string) {
        return this.reportPDFCvdRenderService.GenerateCVDVapor(workbook, worksheetName, cvdExp, subHeader);
    }

    async GenerateCVDStageOil(workbook: Workbook, worksheetName: string, cvdExp: CVDReportData, subHeader: string) {
        return this.reportPDFCvdRenderService.GenerateCVDStageOil(workbook, worksheetName, cvdExp, subHeader);
    }

    async GenerateCVDMaterialBalance(workbook: Workbook, worksheetName: string, cvdExp: CVDReportData, subHeader: string) {
        return this.reportPDFCvdRenderService.GenerateCVDMaterialBalance(workbook, worksheetName, cvdExp, subHeader);
    }

    async GenerateViscosity(workbook: Workbook, worksheetName: string, viscosityReport: viscosityReports, wellName: string, sampleName: string, subHeader: string) {
        return this.reportPDFViscosityRenderService.GenerateViscosity(workbook, worksheetName, viscosityReport, wellName, sampleName, subHeader);
    }

    async GenerateSeparatorTest(workbook: Workbook, worksheetName: string, msstExp: MSSTReportData, wellName: string, sampleName: string, subHeader: string, appendix = '', subHead = false) {
        return this.reportPDFMsstRenderService.GenerateSeparatorTest(workbook, worksheetName, msstExp, wellName, sampleName, subHeader, appendix, subHead);
    }

    async GenerateSeparatorTestVaporCompositions(workbook: Workbook, worksheetName: string, msstExp: MSSTReportData, subHeader: string) {
        return this.reportPDFMsstRenderService.GenerateSeparatorTestVaporCompositions(workbook, worksheetName, msstExp, subHeader);
    }

    async GenerateSeparatorTestResidualOilComp(workbook: Workbook, worksheetName: string, msstExp: MSSTReportData, subHeader: string) {
        return this.reportPDFMsstRenderService.GenerateSeparatorTestResidualOilComp(workbook, worksheetName, msstExp, subHeader);
    }

    async GenerateMaterialBalanceSummary(workbook: Workbook, worksheetName: string, msstExp: MSSTReportData, subHeader: string) {
        return this.reportPDFMsstRenderService.GenerateMaterialBalanceSummary(workbook, worksheetName, msstExp, subHeader);
    }

    async GenerateNParaffinAnalysis(workbook: Workbook, worksheetName: string, paraffinExp: ParaffinReports, wellName: string, sampleName: string, subHeader: string) {
        return this.reportPDFParaffinRenderService.GenerateNParaffinAnalysis(workbook, worksheetName, paraffinExp, wellName, sampleName, subHeader);
    }

    async GenerateFractionAppendix(workbook: Workbook, worksheetName: string, fractionExp: FractionReports, subHeader: string) {
        return this.reportPDFParaffinRenderService.GenerateFractionAppendix(workbook, worksheetName, fractionExp, subHeader);
    }

    async GenerateRecombinationQC(workbook: Workbook, worksheetName: string, bcaExp: RecombinationQCReportData, wellName: string, sampleName: string, subHeader: string) {
        return this.reportPDFRecombinationQcRenderService.GenerateRecombinationQC(workbook, worksheetName, bcaExp, wellName, sampleName, subHeader);
    }

    async GenerateDecontamination(workbook: Workbook, worksheetName: string, docontExp: DecontaminationReportData, wellName: string, sampleName: string, subHeader: string) {
        return this.reportPDFDecontaminationRenderService.GenerateDecontamination(workbook, worksheetName, docontExp, wellName, sampleName, subHeader);
    }

    async GenerateDecontaminationComp(workbook: Workbook, worksheetName: string, docontExp: DecontaminationReportData, wellName: string, sampleName: string, subHeader: string) {
        return this.reportPDFDecontaminationRenderService.GenerateDecontaminationComp(workbook, worksheetName, docontExp, wellName, sampleName, subHeader);
    }

    async GenerateSummaryOfCCE(workbook: Workbook, worksheetName: string, CCEExp: SummaryOfCCEReports, wellName: string, sampleName: string, fromCall = '', letter = '', reportType: string, subHeader: string) {
        return this.reportPDFCceRenderService.GenerateSummaryOfCCE(workbook, worksheetName, CCEExp, wellName, sampleName, fromCall, letter, reportType, subHeader);
    }

    async GenerateCCE(workbook: Workbook, worksheetName: string, CCEExp: CCEReports, wellName: string, sampleName: string, subHeader: string) {
        return this.reportPDFCceRenderService.GenerateCCE(workbook, worksheetName, CCEExp, wellName, sampleName, subHeader);
    }

    async GenerateCCEPlots(workbook: Workbook, worksheetName: string, CCEExp: CCEReports, wellName: string, sampleName: string, subHeader: string) {
        return this.reportPDFCceRenderService.GenerateCCEPlots(workbook, worksheetName, CCEExp, wellName, sampleName, subHeader);
    }

    async GenerateCCEDewPoint(workbook: Workbook, worksheetName: string, CCEDewPointExp: CCEDewPointReports, wellName: string, sampleName: string, subHeader: string) {
        return this.reportPDFCceRenderService.GenerateCCEDewPoint(workbook, worksheetName, CCEDewPointExp, wellName, sampleName, subHeader);
    }

    async GenerateCCEDewPlots(workbook: Workbook, worksheetName: string, CCEDewPointExp: CCEDewPointReports, wellName: string, sampleName: string, subHeader: string) {
        return this.reportPDFCceRenderService.GenerateCCEDewPlots(workbook, worksheetName, CCEDewPointExp, wellName, sampleName, subHeader);
    }
    async GenerateCCESepGas(workbook: Workbook, worksheetName: string, CCEExp: CCEReports, wellName: string, molPercentage = 0, subHeader: string) {
        return this.reportPDFCceRenderService.GenerateCCESepGas(workbook, worksheetName, CCEExp, wellName, molPercentage, subHeader);
    }
    async GenerateCCEGasPlots(workbook: Workbook, worksheetName: string, CCEExp: CCEReports, wellName: string, molPercentage = 0, subHeader: string) {
        return this.reportPDFCceRenderService.GenerateCCEGasPlots(workbook, worksheetName, CCEExp, wellName, molPercentage, subHeader);
    }
    async GenerateCCEDewPointSepGas(workbook: Workbook, worksheetName: string, CCEExp: CCEDewPointReports, wellName: string, molPercentage = 0, subHeader: string) {
        return this.reportPDFCceRenderService.GenerateCCEDewPointSepGas(workbook, worksheetName, CCEExp, wellName, molPercentage, subHeader);
    }

    async GenerateCCEDewPointGasPlots(workbook: Workbook, worksheetName: string, CCEExp: CCEDewPointReports, wellName: string, sampleName: string, molPercentage = 0, subHeader: string) {
        return this.reportPDFCceRenderService.GenerateCCEDewPointGasPlots(workbook, worksheetName, CCEExp, wellName, sampleName, molPercentage, subHeader);
    }
    async GenerateSeparatorOilExcelReport(workbook: Workbook, worksheetName: string, separatorData: SeparatorOilProcessModel, wellName: string, sampleName: string, subHeader: string) {
        return this.reportPDFSeparatorOilRenderService.GenerateSeparatorOilExcelReport(workbook, worksheetName, separatorData, wellName, sampleName, subHeader);
    }

    async GenerateSeparatorOilFlashGORData(workbook: Workbook, worksheetName: string, separatorData: SeparatorOilProcessModel, wellName: string, sampleName: string, subHeader: string) {
        return this.reportPDFSeparatorOilRenderService.GenerateSeparatorOilFlashGORData(workbook, worksheetName, separatorData, wellName, sampleName, subHeader);
    }

    async GenerateSeparatorOilMaterialBalanceHoffmanPlot(workbook: Workbook, worksheetName: string, graphImages: string[], wellName: string, sampleName: string, subHeader: string) {
        return this.reportPDFSeparatorOilRenderService.GenerateSeparatorOilMaterialBalanceHoffmanPlot(workbook, worksheetName, graphImages, wellName, sampleName, subHeader);
    }

    // Excel Swelling Methods
    GenerateSwellingReport(workbook: Workbook, worksheetName: string, swellingExp: SwellingFinalReport, wellName: string, subHeader: string, fromCall = '', letter = '') {
        return this.reportPDFSwellingRenderService.GenerateSwellingReport(workbook, worksheetName, swellingExp, wellName, subHeader, fromCall, letter);
    }

    GenerateSwellingGasCompDataReport(workbook: Workbook, worksheetName: string, swellingExp: SwellingFinalReport, wellName: string, subHeader: string) {
        return this.reportPDFSwellingRenderService.GenerateSwellingGasCompDataReport(workbook, worksheetName, swellingExp, wellName, subHeader);
    }

    GenerateSwellingCalculatedCompBalData(workbook: Workbook, worksheetName: string, swellingExp: SwellingFinalReport, subHeader: string) {
        return this.reportPDFSwellingRenderService.GenerateSwellingCalculatedCompBalData(workbook, worksheetName, swellingExp, subHeader);
    }
    // Separator Gas Excel
    async GenerateSeparatorGasExcelReport(workbook: Workbook, worksheetName: string, separatorData: SeparatorGasReports, subHeader: string) {
        return this.reportPDFSeparatorGasRenderService.GenerateSeparatorGasExcelReport(workbook, worksheetName, separatorData, subHeader);
    }

    public AddOuterBorder(
        worksheet: Worksheet,
        startRowNo: number,
        endRowNo: number,
        startColumnNo: number,
        endColumnNo: number
    ) {
        for (let r = startRowNo; r <= endRowNo; r++) {
            for (let c = startColumnNo; c <= endColumnNo; c++) {
                const cell = worksheet.getCell(r, c);
                // Get the current top border value if it exists, else undefined
                const currentTop = cell.border && cell.border.top ? cell.border.top : undefined;
                const currentBottom = cell.border && cell.border.bottom ? cell.border.bottom : undefined;
                const currentLeft = cell.border && cell.border.left ? cell.border.left : undefined;
                const currentRight = cell.border && cell.border.right ? cell.border.right : undefined;

                // Only set border if cell is on the outer edge
                cell.border = {
                    top: r === startRowNo ? { style: reportConstants.thin } : currentTop,
                    bottom: r === endRowNo ? { style: reportConstants.thin } : currentBottom,
                    left: c === startColumnNo ? { style: reportConstants.thin } : currentLeft,
                    right: c === endColumnNo ? { style: reportConstants.thin } : currentRight,
                };
            }
        }
    }
    GenerateTableWithBorders(
        worksheet: Worksheet,
        tableHeader: string[][],
        tableBody: (string | number)[][],
        tableStartRow: number,
        tableStartColumn: number,
        borderStyle: { top: { style: string; }; left: { style: string; }; bottom: { style: string; }; right: { style: string; } }
    ) {
        // Write header rows
        tableHeader.forEach((headerRow, idx) => {
            headerRow.forEach((header, colIdx) => {

                const cell = worksheet.getCell(tableStartRow + idx, tableStartColumn + colIdx);
                cell.value = header;
                cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle, wrapText: true };
                // Set outer border as bold, inner as thin
                cell.border = {
                    top: idx === 0 ? { style: reportConstants.medium } : borderStyle.top,
                    bottom: idx === tableHeader.length - 1 ? { style: reportConstants.double } : borderStyle.bottom,
                    left: colIdx === 0 ? { style: reportConstants.medium } : borderStyle.left,
                    right: colIdx === headerRow.length - 1 ? { style: reportConstants.medium } : borderStyle.right,
                } as Partial<Borders>;

                const headerValue = header.split('\n');
                if (headerValue && headerValue.length > 0 && headerValue[0].length > 0) {
                    worksheet.getColumn(tableStartColumn + colIdx).width = headerValue[0].length + 2; // Increase column width
                } else {
                    worksheet.getColumn(tableStartColumn + colIdx).width = 8; // Default width if no header value
                }
                worksheet.getRow(tableStartRow + idx).height = 80; // Increase row height for header
            });
        });

        tableBody.forEach((row, rowIdx) => {
            row.forEach((cellValue, colIdx) => {
                const cell = worksheet.getCell(tableStartRow + tableHeader.length + rowIdx, tableStartColumn + colIdx);
                setNumericOrTextCellValue(cell, cellValue);
                cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
                cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                // Set outer border as bold, inner as thin
                cell.border = {

                    bottom: rowIdx === tableBody.length - 1 ? { style: reportConstants.medium } : borderStyle.bottom,
                    left: colIdx === 0 ? { style: reportConstants.medium } : borderStyle.left,
                    right: colIdx === row.length - 1 ? { style: reportConstants.medium } : borderStyle.right,
                } as Partial<Borders>;
            });
        });
    }
    GenerateTableWithBordersNew(
        worksheet: Worksheet,
        tableHeader: string[][],
        tableBody: (string | number)[][],
        tableStartRow: number,
        tableStartColumn: number,
        borderStyle: { top: { style: string; }; left: { style: string; }; bottom: { style: string; }; right: { style: string; } }
    ) {
        // Write header rows
        tableHeader.forEach((headerRow, idx) => {
            headerRow.forEach((header, colIdx) => {

                const cell = worksheet.getCell(tableStartRow + idx, tableStartColumn + colIdx);
                cell.value = header;
                cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: true };
                cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle, wrapText: true };
                // Set outer border as bold, inner as thin
                cell.border = {
                    top: idx === 0 ? { style: reportConstants.medium } : borderStyle.top,
                    bottom: idx === tableHeader.length - 1 ? { style: reportConstants.double } : borderStyle.bottom,
                    left: colIdx === 0 ? { style: reportConstants.medium } : borderStyle.left,
                    right: colIdx === headerRow.length - 1 ? { style: reportConstants.medium } : borderStyle.right,
                } as Partial<Borders>;

                const headerValue = header.split('\n');
                if (headerValue && headerValue.length > 0 && headerValue[0].length > 0) {
                    worksheet.getColumn(tableStartColumn + colIdx).width = 9;//headerValue[0].length + 2; // Increase column width
                } else {
                    worksheet.getColumn(tableStartColumn + colIdx).width = 9; // Default width if no header value
                }
                worksheet.getRow(tableStartRow + idx).height = 80; // Increase row height for header
            });
        });

        tableBody.forEach((row, rowIdx) => {
            row.forEach((cellValue, colIdx) => {
                const cell = worksheet.getCell(tableStartRow + tableHeader.length + rowIdx, tableStartColumn + colIdx);
                setNumericOrTextCellValue(cell, cellValue);
                cell.font = { name: reportConstants.eMprintCondensed, size: 11, bold: false };
                cell.alignment = { horizontal: reportConstants.center, vertical: reportConstants.middle };
                // Set outer border as bold, inner as thin
                cell.border = {

                    bottom: rowIdx === tableBody.length - 1 ? { style: reportConstants.medium } : borderStyle.bottom,
                    left: colIdx === 0 ? { style: reportConstants.medium } : borderStyle.left,
                    right: colIdx === row.length - 1 ? { style: reportConstants.medium } : borderStyle.right,
                } as Partial<Borders>;
            });
        });
    }
    FormatPlotsSheetName(worksheetName: string): string {
        const m = worksheetName.match(/^(.+?)\s*\((\d+)\)\s*$/);
        if (m) {
            const base = m[1].replace(/\s+Plots$/i, '').trim();
            const index = m[2];
            return `${base}-Plots (${index})`;
        }

        const base = worksheetName.replace(/\s+Plots$/i, '').trim();
        return `${base}-Plots`;
    }
    /** Excel sheet name rules: max 31 chars, disallowed: []:/\?* */
    SanitizeSheetName(name: string): string {
        const invalid = /[[\]:/\\?*]/g;
        let s = name.replace(invalid, '_');
        // Trim spaces and limit to 31 chars
        s = s.trim().slice(0, 100);
        // Excel disallows names that look like a single apostrophe
        if (s === '') s = reportConstants.sheet;
        return s;
    }

    /** Return a unique non-colliding sheet name in this workbook. */
    GetUniqueSheetName(workbook: Workbook, baseName: string): string {
        const cleanBase = this.SanitizeSheetName(baseName);
        if (!workbook.getWorksheet(cleanBase)) {
            return cleanBase;
        }
        let i = 1;
        while (true) {
            const candidate = this.SanitizeSheetName(`${cleanBase} (${i})`);
            if (!workbook.getWorksheet(candidate)) {
                return candidate;
            }
            i++;
        }
    }
    ExtractAppendixCode(s: string): string | null {
        // Captures one or more letters after reportConstants.appendix, before a word boundary
        // Works for "Appendix B: ...", "Appendix C-", "Appendix AA", etc.
        const m = s?.match(/Appendix\s+([A-Za-z]+)\b/i);
        return m ? m[1].toUpperCase() : null;
    }
    AddComment(worksheet: Worksheet, column: string, rowNo: number, value: string, fontSize = 11) {
        const textCommentsCell = worksheet.getCell(column + rowNo);
        textCommentsCell.value = value;
        textCommentsCell.font = { name: reportConstants.eMprintCondensed, size: fontSize, bold: false };
        textCommentsCell.alignment = { horizontal: reportConstants.left, vertical: reportConstants.middle };
        worksheet.getRow(rowNo).height = 25; // Adjust row height for comments
    }
}




